import { resolvePlanningBufferPct } from '@/utils/planningBuffer';
import { SchedulingWeatherAdvisory } from '@/components/weather/SchedulingWeatherAdvisory';
import { RankingWeatherNotice, type RankingWeatherAdvisory } from '@/components/weather/RankingWeatherNotice';
import { RankingLiftNotice, rankingLiftNeedsAttention } from '@/components/tickets/RankingLiftNotice';
import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useCanonicalSourceJobLines } from '@/hooks/useCanonicalSourceJobLines';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { createVehicle } from '@/lib/api/vehicleApi';
import { decideTicketVehicleAction } from './ticketVehicleResolution';
import { toast } from '@/hooks/use-toast';
import { Loader2, Trophy, User, Zap, Calendar, Wrench, Clock, AlertCircle, Scissors } from 'lucide-react';
import { DlgAdvisoryBadge, type DlgState } from './DlgAdvisoryBadge';
import { CultureInfluenceNote, isCultureInfluenceList, type CultureInfluenceItem } from './CultureInfluenceNote';
import { DlgOverrideModal, type DlgOverrideReason } from './DlgOverrideModal';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { CalendarModal } from '@/components/layout/CalendarModal';
import { format, parse, isValid, addMinutes, isSameDay, addDays } from 'date-fns';

import { Label } from '@/components/ui/label';
import { validateTicketStartTime } from '@/utils/ticketValidation';
import { smartAssignRefusalMessage } from '@/utils/assignment/smartAssignRefusal';
import { breadcrumbs } from '@/telemetry/breadcrumbs';
import { checkScheduleInvariants } from '@/utils/invariants';
import { summariseInvariantCoverage } from '@/utils/invariantCoverage';
import { recordIntegrityAudit } from '@/utils/operationalIntegrity';
import { SplitPreviewDialog } from './SplitPreviewDialog';
import { generateSplitOptions, SplitOption } from '@/utils/generateSplitOptions';
import { getCategorySkillTier, getProficiencyCategory } from '@/constants/proficiencyCategories';
import { calculateWallMinutes, calculateTicketOptiFinish, parseTimeToMinutes } from '@/utils/canonicalTimeMath';
import { getMinutesInShopTZ } from '@/utils/timezoneMath';
import { useShopTimezone } from '@/contexts/TimezoneContext';
// DTC-E: route the past-time tutorial check + the partA scheduledStart
// fallback through the canonical clock so placement decisions are
// deterministic under `clock.setFixed(...)`. The split_minute write
// at L1805 routes through the canonical shop-tz minute helper because
// `startTimeToUse` is a UTC ISO string — reading raw `.getHours()`
// would return browser-local hours and silently corrupt the split
// boundary when the operator's browser TZ differs from the shop's.
import { clock } from '@/utils/clock';
import { useDate } from '@/contexts/DateContext';
import { useTutorial } from '@/contexts/TutorialContext';
import { convertDatetimeLocalToUTC } from '@/utils/timezoneMath';
import { getShopDayLabel } from '@/utils/shopTime';
import { clearCreateOperation, readCreateOperation, rememberCreateOperationVehicle } from '@/lib/tickets/createTicketOperation';
import { buildRolloverChain, buildOptiPlaceRolloverAttestation, resolveRolloverPartsForStart, type RankingRolloverPart, type RankingRolloverPlan } from '@/utils/rolloverPlanClient';
// Chronomancer parity (R-PAST-1): the same shop-tz-safe fallback the
// Catalyst auto-assign path uses when the suggested time is missing.
// Replaces a pre-existing `selectedDate.toISOString()` browser-local
// fallback that could insert tickets a full day off for shops east of
// the operator's browser timezone.
import { getTodaySafeStartTime } from '@/utils/getTodaySafeStartTime';
import { logTicketCreated, logTicketAssigned } from '@/utils/ticketEventLogger';
import { logTicketEventToOcis } from '@/utils/ocisIngest';
import { useAuth } from '@/contexts/AuthContext';
import { getNextOpenDay, getShopHoursForDay, ShopHours } from '@/utils/shopHoursUtils';
import { parseAppointmentDateAsDate, computeOptiFinishAdvisory } from '@/utils/optiFinishAdvisory';
import { Holiday } from '@/utils/businessDayCalculator';
import { useFeatureFlag, useExposureFlag } from '@/hooks/useFeatureFlags';
import { buildLaborColumnsForAutoWrite } from '@/lib/labor/autoWriteLabor';
import { buildTechPayColumnsForAutoWrite } from '@/lib/labor/autoWriteTechPay';
// Static-imported (was previously `await import(...)`) so the helpers ride
// the main bundle. A deploy that re-hashes the lazy chunk would otherwise
// 404 for browsers still holding the old `index.js`, surfacing as
// "Failed to fetch dynamically imported module" on Create Ticket.
import { pickCompatibleBayForTech } from '@/utils/pickCompatibleBayForTech';
import { isBayCurrentlyOccupied } from '@/utils/bayOccupancyCheck';
import { isSpotCurrentlyOccupied } from '@/utils/spotOccupancyCheck';
import { buildBaySnapshotsForJobs } from '@/utils/catalogBayRules';
import { createTicketBundle } from '@/lib/tickets/createTicketBundle';
import { placementRefusalMessage, placementRefusalReason } from '@/lib/optiplace/placementRefusal';
import { callProposalRefusalMessage } from '@/lib/callProposal/callProposalTicket';
import { AssignmentExplanationView } from '@/components/intelligence/AssignmentExplanationView';
import {
  buildOptiPlaceAnalysisJobs,
  buildSplitProposalKey,
  deriveSplitOperationId,
} from '@/lib/tickets/optiPlaceAnalysisInput';
import { notifyDayboardInvalidated } from '@/utils/dayboardInvalidation';
import { fetchEligibleTechnicianIds, applyEligibility } from '@/lib/roles/eligibilityQuery';
import { readRankingRecord, recordAssignmentSelection, type RankingRecord } from '@/lib/assignment/rankingAuditRecord';
// CORE-AB: the keys the ranker actually sorted on, and which one settled this
// candidate's position. The "Overall Score" beside it is assigned AFTER the
// sort and explains nothing.
import {
  describeRankingBasis,
  RANK_KEY_ORDER,
  RANK_KEY_LABEL,
  applyComebackPin,
  rankingNeighbour,
  describeExclusions,
  DISPLAY_SCORE_LABEL,
  NO_HISTORY_CAVEAT,
  NO_HISTORY_INDICATOR,
  rankIndicatorDisplay,
  DISPLAY_SCORE_CAVEAT,
} from '@/utils/assignment/rankingBasis';

interface TechnicianRanking {
  technicianId: string;
  technicianName: string;
  /**
   * CORE-AB: the "Overall Score". NOT the ranking basis — smart-assign
   * computes it as a weighted sum the sort never uses, then overwrites it
   * post-sort so it decreases monotonically with rank position. It agrees with
   * the order by construction and explains none of it.
   */
  score: number;
  reasoning: string;
  /** Core-100 R4 CORE-BC: the typed explanation (signals, sources, sample sizes, confidence). Absent from older servers. */
  explanation?: unknown;
  /** null = OptiBay has no proficiency history in these categories (unknown, not 0). */
  proficiencyMatch: number | null;

  // ── The actual lexicographic rank keys, in the order the ranker consults
  // them. smart-assign has always returned these; nothing on this screen read
  // them, which is CORE-AB-GAP-001.
  /** Higher wins. THE top key — and it is banded, so 82% and 84% tie. */
  proficiencyBand?: number;
  /** Minutes already booked. Lower wins, only when the bands tie. */
  currentWorkload?: number;
  /** Minutes from midnight of the suggested start. Earlier wins. */
  startMinutes?: number;
  /** Higher wins. */
  historicalAffinityScore?: number;
  /**
   * The PRE-sort value the comparator's fifth key compared, preserved by
   * smart-assign before the post-sort pass rewrites `score`. Absent means the
   * fifth key cannot be read — not that it tied.
   */
  rankTieBreakScore?: number;
  availabilityScore: number;
  workloadScore: number;
  bayMatch: boolean;
  optiTime: number;
  suggestedStartTime: string | null;
  suggestedFinishTime: string | null;
  availabilityPercentage?: number;
  workloadPercentage?: number;
  // Rollover / multi-day support
  willRollover?: boolean;
  isRollover?: boolean;
  /**
   * FLOYD-7 — WHICH DAY this candidate's start falls on.
   *
   * `suggestedStartTime` alone reads as today. A technician whose next
   * opportunity is tomorrow morning must not be shown a bare time an
   * advisor will take for this afternoon.
   */
  startDateStr?: string | null;
  isFutureDay?: boolean | null;
  // Legacy projections of rolloverParts[0] / rolloverParts[1]. The plan is
  // `rolloverParts`, which may hold any number of parts.
  day1Minutes?: number;
  day2Minutes?: number;
  day1StartTime?: string;
  day1FinishTime?: string;
  day2StartTime?: string;
  day2FinishTime?: string;
  rolloverParts?: RankingRolloverPart[];
  /** Whole-chain completion; never a part's own OptiFinish. */
  chainFinishTime?: string | null;
  /** The workdays the server resolved, for re-planning a moved start. */
  rolloverPlan?: RankingRolloverPlan | null;
  /** Core-100 R4 — CORE-BR: smart-assign's weather annotation for an outdoor-bay technician (advisory only). */
  weatherAdvisory?: RankingWeatherAdvisory | null;
  /** Core-100 R4 — CORE-AU (OD-22): every lift of this technician is KNOWN too small for the vehicle. */
  liftSafetyBlock?: boolean;
  liftSafetyReason?: string;
  /** Core-100 R4 — CORE-AU (OD-22): the pairing cannot be shown safe (unknown weight / unrated lift). */
  liftVerificationRequired?: boolean;
  liftVerificationBasis?: 'lift_rating_unknown' | 'vehicle_weight_unknown';
  liftVerificationReason?: string;
  // Split-ticket diagnostics
  willSplit?: boolean;
  splitReason?: string;
  splitPartA?: {
    jobCount: number;
    avgSkillLevel: number;
    minutes: number;
  };
  splitPartB?: {
    jobCount: number;
    avgSkillLevel: number;
    minutes: number;
  };
  // DLG (Diagnostic Load Guard)
  dlgState?: DlgState;
  dlgExplanation?: string;
  dlgSignals?: string[];
  /** CORE-BI: which shop preference shaped THIS candidate, server-authored. */
  cultureInfluence?: CultureInfluenceItem[];
}

interface SplitSuggestion {
  eligible: boolean;
  /** CORE-BI: why the ticket was (or was not) offered as a split, in shop terms. */
  cultureInfluence?: CultureInfluenceItem[];
  splitType?: 'tier-based' | 'none';
  tierGap?: number;
  splitGroups?: Array<{
    jobIds: string[];
    jobs: any[];
    avgSkillLevel: number;
    categories: string[];
    totalMinutes: number;
    suggestedTechId?: string;
    suggestedTechName?: string;
    reasoning: string;
  }>;
}

interface OptiPlaceModalProps {
  isOpen: boolean;
  onClose: () => void;
    ticketData: {
      id?: string; // Optional for reassignment
      status?: string; // Current lifecycle status (present for reassign/split of an existing ticket; used as `fromStatus` in split provenance)
      vehicleId?: string; // For reassignments - existing vehicle ID
      customerId?: string; // Customer ID for linking
      gvwrLbs?: number | null; // Vehicle GVWR for lift safety
      vehicle?: {
        year: number;
        make: string;
        model: string;
        engineSize?: string;
        vin?: string;
        licensePlate?: string;
      };
      customerName?: string;
      customerPhone?: string;
      customerComplaint?: string; // Customer complaint text for customer_request_notes
      type: string;
      priority: string;
      appointmentDate: string;
      dueOut?: string;
      /** The durable Create Ticket operation id (createTicketOperation.ts). */
      createOperationId?: string;
      /**
       * Core-100 R4 CORE-Y: how this NEW ticket originated — typed, prefilled from an uploaded repair order, or
       * prefilled from a conversation (tickets.creation_method, recorded once). Absent = typed ('create_ticket').
       */
      creationMethod?: 'create_ticket' | 'document_upload' | 'conversation';
      /**
       * Core-100 R5 (CORE-AQ): the call appointment proposal this NEW ticket is created from. Written once on the
       * primary ticket (tickets.source_call_proposal_id); the database confirms the proposal against it in the same
       * transaction, or refuses the whole creation (stale / decided / other shop / not front office).
       */
      sourceCallProposalId?: string;
      odometerMiles?: number; // Odometer reading at time of service
      isComeback: boolean;
      startTime?: string; // Optional fixed start time for waiter tickets (HH:mm)
      splitParentId?: string | null; // Track if this is already a split child
  jobs: Array<{
    category: string;
    submenu: string;
    name: string;
    minutes: number;
    bay: string;
    skill: number;
    proficiency_category?: string;
    catalog_job_id?: string;
    notes?: string;
    /** Phase 4B: stable client-side key used to map staged parts to the real job_id after insert. */
    tempJobId?: string;
  }>;
      predictedMinutes: number;
      parkingSpotId?: string | null;
      /** Phase 4B: staged parts persisted into parts_lines after ticket_jobs are created. */
      stagedParts?: Array<{
        tempId: string;
        tempJobId: string | null;
        description: string;
        vendor_name: string | null;
        vendor_part_number: string | null;
        category: string | null;
        quantity: number;
        unit_cost: number | null;
        unit_sell: number | null;
        taxable: boolean;
        core_charge: number;
        notes: string | null;
      }>;
    };
  onAssignmentComplete: (ticketId: string) => void;
  /** Core-100 R5 (CORE-AQ): the server refused the ticket for its call proposal — nothing was created. */
  onCallProposalRefused?: (message: string) => void;
}

export const OptiPlaceModal = ({ isOpen, onClose, ticketData, onAssignmentComplete, onCallProposalRefused }: OptiPlaceModalProps) => {
  // DCD-A1: dropped unused `setSelectedDate` destructure — only the
  // canonical `setSelectedDayLabel` setter is invoked in this file
  // (L271, L494, L2537). The deprecated browser-Date setter is no
  // longer referenced here, narrowing the consumer surface for its
  // eventual removal.
  const { selectedDate, selectedDayLabel, setSelectedDayLabel } = useDate();
  const { currentStepData, nextStep } = useTutorial();
  const { activeShop } = useAuth();
  const laborEngineEnabled = useFeatureFlag('labor_engine_enabled');
  const ticketFinancialsEnabled = useExposureFlag('ui_ticket_financials');
  const techPayEnabled = useExposureFlag('ui_technician_pay');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [rankings, setRankings] = useState<TechnicianRanking[]>([]);
  // FLOYD-7 — smart-assign sets this when NOBODY can start on the requested
  // date and the list is therefore next-available options. It was returned
  // and read by nothing, so the advisor saw an ordinary ranked list.
  const [isNextAvailableOnly, setIsNextAvailableOnly] = useState(false);
  const [rolloverOnlyStartsToday, setRolloverOnlyStartsToday] = useState(false);

  /**
   * CORE-AB: the technician the comeback override pinned to #1, if any. Set
   * only when the pin actually moved somebody, so the surface never reports
   * an override that did not happen.
   */
  const [comebackPinnedTechId, setComebackPinnedTechId] = useState<string | null>(null);

  // Store ALL raw rankings from smart-assign (including filtered-out techs) for audit logging.
  //
  // DECLARED HERE, ABOVE ITS FIRST READER, AND IT HAS TO STAY THERE. The
  // `rankingExclusions` memo below reads this ref, and a useMemo factory is
  // not deferred — React runs it synchronously during the same render. When
  // this `const` sat ~70 lines lower the first render read it inside its
  // temporal dead zone and threw `Cannot access 'allRawRankingsRef' before
  // initialization`, which minified to `Cannot access 'Ft' before
  // initialization` and put every Create Ticket attempt into the
  // ErrorBoundary. tests/unit/tickets/optiPlaceModalInitialRender.test.tsx
  // renders this component so that ordering is enforced by execution rather
  // than by reading the file.
  const allRawRankingsRef = useRef<any[]>([]);
  // Core-100 R5 (CORE-AB S): the ranking run smart-assign recorded for the analysis on screen (server-authored).
  const rankingRecordRef = useRef<RankingRecord | null>(null);

  /** Technicians a hard constraint removed. Absent is not the same as last. */
  const rankingExclusions = useMemo(
    () => describeExclusions((allRawRankingsRef.current || []) as never, rankings as never),
    [rankings],
  );
  const [selectedTechId, setSelectedTechId] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  // ONE create attempt per opening of OptiPlace. A confirm that times out at
  // the gateway may still have committed (create_ticket_bundle is atomic but
  // the browser can lose the answer), so a retry must reuse the same ticket id,
  // the same vehicle and the same rollover chain ids — the database's primary
  // keys then make a second copy impossible — and must first look for the
  // ticket the earlier attempt may already have created.
  const createAttemptRef = useRef<{ ticketId: string; vehicleId: string | null; chainNowMs: number; submitted: boolean } | null>(null);
  useEffect(() => { if (!isOpen) createAttemptRef.current = null; }, [isOpen]);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  
  const [lastTechId, setLastTechId] = useState<string | null>(null);
  const [shopHours, setShopHours] = useState<ShopHours | null>(null);
  const [holidays, setHolidays] = useState<Holiday[]>([]);
  const [splitSuggestion, setSplitSuggestion] = useState<SplitSuggestion | null>(null);
  // CORE-BI: a shop preference that HELD BACK a split is shown even though no split card appears.
  const [splitHeldBackBy, setSplitHeldBackBy] = useState<CultureInfluenceItem[]>([]);
  const [showSplitPreview, setShowSplitPreview] = useState(false);
  const [splitOptions, setSplitOptions] = useState<SplitOption[]>([]);
  const [currentSplitOptionIndex, setCurrentSplitOptionIndex] = useState(0);
  const [isSplitting, setIsSplitting] = useState(false);
  const [shopCulture, setShopCulture] = useState<any>(null);
  const [dlgOverrideOpen, setDlgOverrideOpen] = useState(false);
  const [pendingDlgTechId, setPendingDlgTechId] = useState<string | null>(null);
  const { shopTimezone } = useShopTimezone();
  
  // Track bay assignment source for reassignment mode (manual bay protection)
  const [bayAssignmentSource, setBayAssignmentSource] = useState<string | null>(null);

  /**
   * 8G.2D.2D — CANONICAL SOURCE JOB LINES for a PERSISTED ticket.
   *
   * MEASURED DEFECT this replaces: no caller ever supplied them.
   * `OptiboardTicketRow` passes `jobs: []` with a comment saying OptiPlace
   * populates them from ticket_jobs — it never did. `AiIntakeLab` passes `[]`
   * too. `EODRolloverContainer` does map real rows, but into
   * {category, submenu, name, hours, minutes, bay, skill} with NO id at all.
   * OptiPlace then minted `job-${idx}-${Math.random()}` as the identity it
   * planned with, and the persisted path cloned that plan into brand-new
   * `jobs` rows instead of moving the source's lines.
   *
   * These rows come straight from the database and carry `ticket_jobs.job_id`
   * as their identity. Nothing here is correlated back to `ticketData.jobs` by
   * name, index, category or catalog id — the DB result IS the identity.
   *
   * 8G.2D.2D.1 §4 — hydration is a STATE, not a timeout. The modal previously
   * ran its first analysis on `setTimeout(..., 50)`, independent of this read.
   * 50ms is a guess about the network, and a persisted structural split must
   * never be decided from job data that had simply not arrived yet.
   *
   * 8G.2D.2D.2 §2 — `sourceJobLinesTicketId` is the ticket the rows were read
   * FOR. Without it, changing the source while the modal is open leaves the
   * previous ticket's canonical lines sitting at `ready`.
   *
   * 8G.2D.2D.3 §6 — the read now lives in a hook that owns its own request
   * generation. The guard that used to sit inline here compared two values
   * captured by the SAME effect closure, so it could never reject anything;
   * see `useCanonicalSourceJobLines` for the full account.
   */
  const {
    sourceJobLines,
    sourceJobLinesTicketId,
    hydration: sourceJobsHydration,
  } = useCanonicalSourceJobLines(isOpen, ticketData.id);
  const isManualBay = bayAssignmentSource === 'manual';

  // Track if auto-advance has run this modal session (prevents loops)
  const [hasAutoAdvanced, setHasAutoAdvanced] = useState(false);
  
  // Track analyzed date for auto-reanalyze debounce
  const [analyzedDate, setAnalyzedDate] = useState<Date | null>(null);
  
  // Message when no availability found within lookahead
  const [noAvailabilityMessage, setNoAvailabilityMessage] = useState<string | null>(null);
  
  // Track if we're currently auto-advancing to prevent re-triggering analysis
  const isAutoAdvancingRef = useRef(false);
  
  // (allRawRankingsRef is declared above, beside the memo that reads it.)

  // Fetch bay_assignment_source for existing tickets (reassignment mode)
  useEffect(() => {
    if (isOpen && ticketData.id) {
      // Fetch current ticket's bay_assignment_source
      supabase
        .from('tickets')
        .select('bay_assignment_source')
        .eq('id', ticketData.id)
        .single()
        .then(({ data }) => {
          setBayAssignmentSource(data?.bay_assignment_source || null);
        });
    } else {
      setBayAssignmentSource(null);
    }
    // Canonical job-line hydration is NOT here: it owns a request generation
    // and lives in `useCanonicalSourceJobLines`, above.
  }, [isOpen, ticketData.id]);

  /**
   * 8G.2D.2D.1 §4 — re-run analysis when canonical hydration lands.
   *
   * Modal startup fires its first analysis on a 50ms timer. For a persisted
   * source that analysis defers itself if the job lines have not arrived, so
   * something has to run it once they have. This is that something: it is
   * driven by the hydration STATE resolving, not by a longer guess about how
   * slow the network might be.
   */
  useEffect(() => {
    if (!isOpen || !ticketData.id) return;
    if (sourceJobsHydration !== 'ready') return;
    if (sourceJobLinesTicketId !== ticketData.id) return;
    if (!sourceJobLines || sourceJobLines.length === 0) return;
    analyzeAssignmentsForDate(selectedDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, ticketData.id, sourceJobsHydration, sourceJobLines, sourceJobLinesTicketId]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setHasAutoAdvanced(false);
      setAnalyzedDate(null);
      setNoAvailabilityMessage(null);
      isAutoAdvancingRef.current = false;
    }
  }, [isOpen]);

  // Auto-advance to next valid day on modal open (guarded to prevent loops)
  useEffect(() => {
    if (!isOpen || !shopTimezone || hasAutoAdvanced) return;
    
    const initializeModal = async () => {
      // Load shop hours and holidays first
      const result = await loadShopHours();
      if (!result?.hours) return;
      
      // Shop hours/holidays were loaded above; the canonical
      // post-analysis no-availability handler (~L498) consumes them
      // via component state. The previous pre-flight that compared the
      // requested date against shop hours and auto-advanced is gone —
      // see the comment block below.

      // Authoritative target = the caller-supplied appointmentDate. The
      // form sets this from selectedDayLabel (CreateTicket.tsx:340) so the
      // top-bar / inline-calendar choice flows through.
      //
      // The prior implementation pre-flighted with isValidSchedulingDate
      // and auto-advanced when shop-hours validation said the day was
      // "closed." That gate over-fires: shops with Saturday techs (FOH,
      // weekend specialists, after-hours staff) have legitimate tech
      // availability on days the shop is "officially" closed —
      // auto-advancing dropped the operator on a different day before
      // smart-assign even ran. We now always start analysis on the
      // requested date and let smart-assign be the authority. The
      // existing post-analysis "no availability" handler in
      // analyzeAssignmentsForDate (~L498 in this file) still catches the
      // truly-unschedulable case and advances when needed.
      const requestedDate =
        parseAppointmentDateAsDate(ticketData.appointmentDate) ?? selectedDate;

      const targetDate = requestedDate;

      // Sync DateContext to the requested date so the inline calendar
      // and any date-driven effects reflect what the modal is analyzing.
      // The ref guard prevents this change from re-triggering analysis
      // in a loop through the auto-reanalyze effect below.
      if (!isSameDay(requestedDate, selectedDate)) {
        isAutoAdvancingRef.current = true;
        setSelectedDayLabel(format(requestedDate, 'yyyy-MM-dd'));
      }
      
      setHasAutoAdvanced(true);
      
      // Run initial analysis with the correct target date (not relying on state)
      // Small delay to let state settle, but pass date directly to avoid race condition
      setTimeout(() => {
        isAutoAdvancingRef.current = false;
        analyzeAssignmentsForDate(targetDate);
        findLastTechnician();
      }, 50);
    };
    
    initializeModal();
  }, [isOpen, shopTimezone, hasAutoAdvanced]);

  // Auto-reanalyze when date changes (debounced 300ms)
  useEffect(() => {
    if (!isOpen || !analyzedDate || isAutoAdvancingRef.current) return;
    
    // Skip if date hasn't actually changed
    if (isSameDay(selectedDate, analyzedDate)) return;
    
    const timeout = setTimeout(() => {
      console.log('[OPTIPLACE] Auto-reanalyzing for new date:', format(selectedDate, 'yyyy-MM-dd'));
      analyzeAssignments();
    }, 300);
    
    return () => clearTimeout(timeout);
  }, [selectedDate, isOpen, analyzedDate]);

  // Keyboard shortcuts for selecting technicians
  useEffect(() => {
    if (!isOpen || rankings.length === 0 || isCreating) return;

    const handleKeyPress = (e: KeyboardEvent) => {
      // Check if a number key was pressed (1-9, 0)
      const key = e.key;
      if (!/^[0-9]$/.test(key)) return;

      const num = parseInt(key);
      // Map 1-9 to indices 0-8, and 0 to index 9
      const index = num === 0 ? 9 : num - 1;

      // Only proceed if we have a ranking at this index
      if (index < rankings.length) {
        const ranking = rankings[index];
        setSelectedTechId(ranking.technicianId);
        // Small delay to ensure selection is set before creating
        setTimeout(() => {
          const createButton = document.querySelector('[data-create-ticket-btn]') as HTMLButtonElement;
          createButton?.click();
        }, 50);
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [isOpen, rankings, isCreating]);

  const findLastTechnician = async () => {
    try {
      // Use vehicle ID directly — VIN/plate are encrypted and cannot be matched client-side
      const vehicleId = ticketData.vehicleId;
      if (!vehicleId) return;

      const vehicleIds = [vehicleId];

      // Find the most recent completed ticket for these vehicles
      const { data: lastTicket } = await supabase
        .from('tickets')
        .select('assigned_tech_id')
        .in('vehicle_id', vehicleIds)
        .eq('status', 'Completed')
        // CSI-S1: never seed last-tech from a soft-deleted Completed ticket.
        // Defense-in-depth alongside DMC-D1's ghost-row write-side fix.
        .is('deleted_at', null)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (lastTicket?.assigned_tech_id) {
        setLastTechId(lastTicket.assigned_tech_id);
      }
    } catch (error) {
      console.error('Error finding last technician:', error);
    }
  };

  const loadShopHours = async () => {
    try {
      // OPS-A6: explicit shop_id scoping. Pre-A6 this query relied on
      // RLS + `.limit(1).maybeSingle()` to return the user's shop_profile.
      // For multi-shop users (ops staff with multi-tenant access in one
      // session), RLS-scoped row order is undefined — `.limit(1)` could
      // hydrate the wrong shop's hours/culture/holidays into modal state.
      // Mirrors the canonical smart-assign pattern at
      // supabase/functions/smart-assign/index.ts:195-199.
      const shopId = activeShop?.id;
      if (!shopId) return null;
      const { data } = await supabase
        .from('shop_profile')
        .select('hours, shop_id, shop_culture, holidays')
        .eq('shop_id', shopId)
        .maybeSingle();
      const hrs = data ? ((data.hours as any) || null) : null;
      const culture = data ? ((data.shop_culture as any) || null) : null;
      const hols: Holiday[] = data?.holidays && Array.isArray(data.holidays) ? (data.holidays as unknown as Holiday[]) : [];
      if (hrs) setShopHours(hrs);
      if (culture) setShopCulture(culture);
      setHolidays(hols);
      return { hours: hrs, shopId: data?.shop_id, holidays: hols };
    } catch (e) {
      console.error('Failed to load shop hours', e);
      return null;
    }
  };
  
  // Analyze assignments for a specific date (avoids race condition with state)
  const analyzeAssignmentsForDate = async (targetDate: Date) => {
    setIsAnalyzing(true);
    setRankings([]);
    setSplitSuggestion(null);
    setNoAvailabilityMessage(null);
    setAnalyzedDate(targetDate); // Track which date was analyzed
    try {
      // Use activeShop from context for proper RLS compliance
      const shopId = activeShop?.id;
      if (!shopId) {
        throw new Error('No active shop selected');
      }
      // Load shop hours for local state
      await loadShopHours();

      const isWaiter = String(ticketData.type || '').toLowerCase() === 'waiter';
      const fixedStartTime = isWaiter && ticketData.startTime ? ticketData.startTime : null;

      // ══ 8G.2D.2D.1 §3/§5 — CANONICAL ANALYSIS INPUT ═════════════════════
      // This used to spread `ticketData` and send its `jobs` straight through.
      // For a persisted Optiboard source that array is `[]`, and smart-assign
      // gates split analysis on `jobs.length >= 2` (index.ts:580) — so the gate
      // never opened, no splitSuggestion was produced, Preview Split could not
      // be entered, and the canonical confirm path this component gained in
      // 8G.2D.2D was unreachable from the real product.
      //
      // The jobs now come from the hydrated `ticket_jobs` rows, carrying the
      // same `job_id` values that later reach the structural authority — so
      // what the operator is shown and what actually moves cannot drift apart.
      const analysisInput = buildOptiPlaceAnalysisJobs({
        ticketData,
        sourceJobLines,
        sourceJobLinesTicketId,
        hydration: sourceJobsHydration,
        categorySkillTier: getCategorySkillTier,
      });

      if (!analysisInput.splitAnalysisReady) {
        // Not a timing guess: the canonical lines have genuinely not arrived,
        // or failed to. A hydration-driven effect re-runs this when they do.
        console.log(`[OPTIPLACE] analysis deferred: ${analysisInput.blockedReason}`);
        setIsAnalyzing(false);
        return;
      }

      const { data, error } = await supabase.functions.invoke('smart-assign', {
        body: { 
          ticketData: {
            ...ticketData,
            shop_id: shopId,
            appointmentDate: format(targetDate, 'yyyy-MM-dd'),
            isWaiter,
            fixedStartTime,
          },
          // Core-100 R5 (CORE-AB S): the server records this ranking; the selection is attached after creation.
          recordRanking: true,
        }
      });

      if (error) throw error;
      
      // [FRONTEND-DEBUG] Log raw smart-assign response
      console.log('[OPTIPLACE-RAW-RESPONSE]', {
        success: !!data,
        error: error,
        techniciansReturned: data?.rankings?.length || 0,
        allTechs: data?.rankings?.map((r: any) => ({
          name: r.technicianName,
          score: r.score,
          proficiency: r.proficiencyMatch,
          tierGap: r.tierGapPenalty,
          canSchedule: r.canSchedule,
          startTime: r.suggestedStartTime,
        })) || []
      });
      
      // FLOYD-7 — record whether this whole list is next-available rather
      // than today. `isRolloverOnly` is the server's name for it.
      setIsNextAvailableOnly(!!(data as { isRolloverOnly?: boolean })?.isRolloverOnly);
      // Floyd round 2 — the rollover-only list is also what the server sends
      // when nobody can FINISH today, and those options may well START today.
      // Say which: "cannot start" is only true when no option begins today.
      setRolloverOnlyStartsToday(
        ((data?.rankings ?? []) as Array<{ isFutureDay?: boolean | null }>).some((r) => r.isFutureDay === false),
      );

      // Capture split suggestion if returned
      if (data.splitSuggestion?.eligible) {
        setSplitSuggestion(data.splitSuggestion);
        console.log('[SPLIT-SUGGESTION] Ticket eligible for split:', data.splitSuggestion);
      }
      setSplitHeldBackBy(
        !data.splitSuggestion?.eligible && isCultureInfluenceList(data.splitSuggestion?.cultureInfluence)
          ? data.splitSuggestion.cultureInfluence
          : [],
      );
      
      // Store audit data from edge function (includes ALL techs, even rejected ones)
      // Falls back to raw rankings if auditData not available
      allRawRankingsRef.current = data.auditData || data.rankings || [];
      rankingRecordRef.current = readRankingRecord(data);
      
      // Accept all valid rankings with either concrete times OR explicit rollover flag
      let filteredRankings = (data.rankings || []).filter((r: TechnicianRanking | any) =>
        typeof r.score === 'number' &&
        !Number.isNaN(r.score) &&
        (
          (r.suggestedStartTime && r.suggestedFinishTime) ||
          r.willRollover ||
          (r as any).isRollover
        )
      );
      
      // COMEBACK OVERRIDE (product ruling).
      //
      // The technician who performed the original work is pinned to #1. That
      // override affects ONLY that technician's position — everyone else
      // keeps the exact relative order smart-assign returned.
      //
      // This previously re-sorted the remainder by `score`, which
      // smart-assign overwrites AFTER sorting purely so the displayed number
      // steps down with rank. Ordering by it discarded proficiency band,
      // workload and earliest-start for every technician below the pin. It
      // also boosted the pinned technician's `score` by 10, and that
      // falsified value was written to assignment_audit as their
      // fitness_score. Both are gone: the pin moves a position and changes
      // nothing else.
      setComebackPinnedTechId(null);
      if (ticketData.isComeback && lastTechId && filteredRankings.length > 0) {
        const pinned = applyComebackPin(filteredRankings, lastTechId);
        if (pinned !== filteredRankings) {
          setComebackPinnedTechId(lastTechId);
          filteredRankings = pinned;
        }
      }
      
      // If no valid techs, check if ANY techs have bay assignments before looping through days
      if (filteredRankings.length === 0 && shopHours && shopTimezone) {
        // Fast-fail: if no active technicians have bay assignments, stop immediately
        const { data: rosterWithBays } = await supabase
          .from('technicians')
          .select('id, bay_assignments')
          .eq('shop_id', activeShop?.id || '')
          .eq('employment_status', 'Active');
        // WS-ELIG B-R3: only people who may receive NEW work count (the one W3
        // gate: observe = the roster unchanged; enforce = operational only).
        const techsWithBays = applyEligibility(
          rosterWithBays || [],
          await fetchEligibleTechnicianIds(activeShop?.id || '', []),
        );

        const anyTechHasBays = techsWithBays.some(t => {
          const assignments = t.bay_assignments as string[] | null;
          return assignments && assignments.length > 0;
        });
        
        if (!anyTechHasBays) {
          setNoAvailabilityMessage('No technicians have bay assignments. Assign bays in Shop Profile first.');
          setIsAnalyzing(false);
          return;
        }
        
        const nextOpen = getNextOpenDay(addDays(selectedDate, 1), shopHours, holidays, shopTimezone, 14);
        
        if (nextOpen && !isSameDay(nextOpen, selectedDate)) {
          console.log('[OPTIPLACE] No techs available, advancing to', format(nextOpen, 'yyyy-MM-dd'));
          isAutoAdvancingRef.current = true;
          setSelectedDayLabel(format(nextOpen, 'yyyy-MM-dd'));
          // Will trigger auto-reanalyze via useEffect
          setIsAnalyzing(false);
          setTimeout(() => {
            isAutoAdvancingRef.current = false;
          }, 100);
          return;
        } else if (!nextOpen) {
          // No valid day found within 14 days
          setNoAvailabilityMessage('No technician availability found within the next 14 days');
        }
      }
      
      setRankings(filteredRankings);
      if (filteredRankings.length > 0) {
        setSelectedTechId(filteredRankings[0].technicianId);
      }
    } catch (error: unknown) {
      // Only a sanctioned smart-assign refusal is shown verbatim (e.g. "All
      // suggested start times are in the past…"); anything else gets the
      // fixed fallback — never a raw exception, body or transport message.
      toast({
        title: 'Analysis Error',
        description: await smartAssignRefusalMessage(error),
        variant: 'destructive',
      });
    } finally {
      setIsAnalyzing(false);
    }
  };

  // Convenience wrapper that uses current selectedDate from state
  const analyzeAssignments = () => analyzeAssignmentsForDate(selectedDate);

  // Handle preview split options button
  const handlePreviewSplit = async () => {
    if (!splitSuggestion || !splitSuggestion.eligible) return;
    
    // GUARD: Never split tickets that are already split children
    if (ticketData.splitParentId) {
      toast({
        title: 'Cannot Split',
        description: 'This ticket is already a split child and cannot be split further',
        variant: 'destructive',
      });
      return;
    }
    
    // Show loading state immediately
    setSplitOptions([]);
    setCurrentSplitOptionIndex(0);
    setShowSplitPreview(true);
    setIsAnalyzing(true);
    
    try {
      // Use activeShop from context for proper RLS compliance
      const shopId = activeShop?.id;
      if (!shopId) {
        toast({
          title: 'Error',
          description: 'No active shop selected',
          variant: 'destructive',
        });
        setIsAnalyzing(false);
        return;
      }
      
      // Fetch technicians with full details (scope to this shop).
      // DMC-G2: dropped unused `shift_start` from the projection.
      // The downstream techs-for-split mapping at the lines below only
      // consumes id/name/proficiencies/skillTier/bayAssignments — the
      // legacy column was selected but never read.
      const { data: technicians } = await supabase
        .from('technicians')
        .select('id, name, proficiencies, skill_tier, bay_assignments, efficiency_percentage, buffer_percentage, recurring_breaks')
        .eq('shop_id', shopId)
        .eq('employment_status', 'Active');
      
      if (!technicians) {
        toast({
          title: 'Error',
          description: 'Could not load technicians for split analysis',
          variant: 'destructive',
        });
        return;
      }
      
      // WORK FAMILY.
      //
      // The split planner proposes who does each part. Planning a part onto
      // somebody who does not perform that kind of work produces an option
      // that looks reasonable on screen and fails at the moment somebody
      // accepts it — the worst place to discover it, because by then an
      // advisor has told a customer a time.
      //
      // The list comes from the server, decided by the same function the
      // assignment trigger uses. When the question cannot be answered the
      // roster is left alone rather than emptied: the database still refuses
      // an invalid option, so the cost is a bad suggestion — and the cost of
      // the alternative is a shop with nobody on its board.
      const splitCategories = [...new Set(
        (ticketData.jobs ?? [])
          .map((j: { proficiency_category?: string; category?: string }) =>
            j.proficiency_category ?? j.category)
          .filter((c): c is string => typeof c === 'string' && c.length > 0),
      )];
      const eligibleForSplit = await fetchEligibleTechnicianIds(shopId, splitCategories);
      const splitCandidates = applyEligibility(technicians, eligibleForSplit);

      if (splitCandidates.length === 0) {
        toast({
          title: 'Nobody performs this work',
          description:
            'No technician in this shop performs every kind of work on this ticket. '
            + 'Check what each person does in Team Management.',
          variant: 'destructive',
        });
        setIsAnalyzing(false);
        return;
      }

      // Convert technicians to expected format
      const techsForSplit = splitCandidates.map(t => ({
        id: t.id,
        name: t.name,
        proficiencies: (t.proficiencies as Record<string, number>) || {},
        skillTier: t.skill_tier || 3,
        bayAssignments: (t.bay_assignments as string[]) || [],
      }));
      
      // ══ 8G.2D.2D — IDENTITY BOUNDARY ═══════════════════════════════════
      // PERSISTED source: the splitter plans over the source's REAL job lines,
      // and every option id is a `ticket_jobs.job_id`. A synthetic id can no
      // longer reach a structural act, because for a persisted ticket no
      // synthetic id is ever created.
      //
      // UNPERSISTED planning: there is no source and nothing to move, so a
      // disposable client key remains correct — it only has to correlate
      // planning objects with rows this same flow is about to insert.
      // Same builder as the analysis payload, so the ids the splitter plans
      // with are exactly the ids smart-assign was given and the ids the
      // authority will move. One identity source, not two that must agree.
      const isPersistedSource = !!ticketData.id;
      const jobsWithIds = isPersistedSource
        ? buildOptiPlaceAnalysisJobs({
            ticketData, sourceJobLines, sourceJobLinesTicketId,
            hydration: sourceJobsHydration,
            categorySkillTier: getCategorySkillTier,
          }).jobs
        : ticketData.jobs.map((job, idx) => ({
        // normalize to the fields generateSplitOptions expects
        id: `job-${idx}-${Math.random().toString(36).substr(2, 9)}`,
        name: job.name,
        category: job.category,
        submenu: job.submenu ?? job.category,
        // Use category-based skill tier instead of job-level skill
        skill: getCategorySkillTier(job.category),
        minutes: job.minutes ?? (job as any).standard_minutes ?? (job as any).standardMinutes ?? 0,
        bay: job.bay ?? (job as any).bay_requirement ?? "",
        proficiency_category: job.proficiency_category ?? job.category,
      }));
      
      const options = generateSplitOptions(
        jobsWithIds,
        techsForSplit,
        0, // proficiencyGap (legacy, no longer used)
        splitSuggestion.tierGap ?? 0, // tierGap for tier-based splitting
        ticketData.splitParentId // Pass split parent ID to guard against recursive splits
      );
      
      // Build tech metadata (efficiency, buffer, breaks)
      const techMeta = new Map<string, { eff: number; buf: number; breaks: { start: number; end: number }[] }>();
      technicians.forEach((t: any) => {
        const breaksRaw = (t.recurring_breaks as any[]) || [];
        const parsedBreaks = breaksRaw
          .map((b: any) => ({ start: parseTimeToMinutes(b.start), end: parseTimeToMinutes(b.end) }))
          .filter((b: any) => !isNaN(b.start) && !isNaN(b.end));
        techMeta.set(t.id, {
          eff: t.efficiency_percentage || 100,
          buf: resolvePlanningBufferPct(t.buffer_percentage),
          breaks: parsedBreaks,
        });
      });

      // Preload schedules for suggested techs on the selected date
      const suggestedTechIds = Array.from(new Set(
        options.flatMap((o) => [o.partA.suggestedTechId, o.partB.suggestedTechId]).filter(Boolean)
      ));
      const dayStr = format(selectedDate, 'yyyy-MM-dd');
      const dayStartISO = `${dayStr}T00:00:00.000Z`; // Used as date anchor for minute-of-day calculations

      const scheduleMap = new Map<string, any[]>();
      if (suggestedTechIds.length > 0) {
        const { data: scheduledTickets } = await supabase
          .from('tickets')
          .select('id, assigned_tech_id, start_time, predicted_minutes, status')
          .in('assigned_tech_id', suggestedTechIds)
          .eq('status', 'Active')
          .eq('scheduled_date', dayStr); // R14-M-1: canonical day scoping
        (scheduledTickets || []).forEach((t: any) => {
          const arr = scheduleMap.get(t.assigned_tech_id) || [];
          arr.push(t);
          scheduleMap.set(t.assigned_tech_id, arr);
        });
      }
      
      // Get actual times from smart-assign for each split option
      const { calculateAvgProficiency } = await import('@/utils/proficiencyMatcher');
      
      // Call smart-assign for BOTH parts in parallel for faster preview
      const smartAssignPromises = options.map(async (option) => {
        const promisesForOption: any[] = [];
        
        // Part A smart-assign (always call, even if no suggested tech yet)
        promisesForOption.push(
          supabase.functions.invoke('smart-assign', {
            body: {
              ticketData: {
                shop_id: shopId,
                type: 'Drop-off',
                appointmentDate: format(selectedDate, 'yyyy-MM-dd'),
                predictedMinutes: option.partA.totalMinutes,
                fixedStartTime: null,
                dueOut: ticketData.dueOut || null,
                jobs: option.partA.jobs.map(j => ({
                  id: j.id,
                  name: j.name,
                  category: j.category,
                  category_key: j.category, // CRITICAL: category is already the category_key from CreateTicket
                  proficiency_category: getProficiencyCategory(j.category),
                  skill: j.skill,
                  minutes: j.minutes,
                  bay: j.bay || '',
                })),
              },
              isScheduledTime: false,
              skipSplitCheck: true,
            },
          }).then(({ data }) => ({ part: 'A', data, option }))
        );
        
        // Part B smart-assign (always call, even if no suggested tech yet)
        promisesForOption.push(
          supabase.functions.invoke('smart-assign', {
            body: {
              ticketData: {
                shop_id: shopId,
                type: 'Drop-off',
                appointmentDate: format(selectedDate, 'yyyy-MM-dd'),
                predictedMinutes: option.partB.totalMinutes,
                fixedStartTime: null,
                dueOut: ticketData.dueOut || null,
                jobs: option.partB.jobs.map(j => ({
                  id: j.id,
                  name: j.name,
                  category: j.category,
                  category_key: j.category, // CRITICAL: category is already the category_key from CreateTicket
                  proficiency_category: getProficiencyCategory(j.category),
                  skill: j.skill,
                  minutes: j.minutes,
                  bay: j.bay || '',
                })),
              },
              isScheduledTime: false,
              skipSplitCheck: true,
            },
          }).then(({ data }) => ({ part: 'B', data, option }))
        );
        
        return Promise.all(promisesForOption);
      });
      
      // Wait for all smart-assign calls to complete
      const allResults = await Promise.all(smartAssignPromises);
      
      // Process results and update options with overlap checking
      allResults.flat().forEach((result) => {
        if (!result) return;
        
        const { part, data, option } = result;
        
        if (part === 'A' && data?.rankings) {
          // Choose the suggested tech if provided; otherwise, fallback to top-ranked
          let techARanking = null as any;
          if (option.partA.suggestedTechId) {
            techARanking = data.rankings.find(
              (r: any) => r.technicianId === option.partA.suggestedTechId
            );
          }
          if (!techARanking && data.rankings?.[0]) {
            techARanking = data.rankings[0];
            option.partA.suggestedTechId = techARanking.technicianId;
            option.partA.suggestedTechName = techARanking.technicianName;
            option.partA.suggestedStartTime = techARanking.suggestedStartTime;
          }
          if (techARanking) {
            const techId = option.partA.suggestedTechId as string;
            const meta = techMeta.get(techId)!;
            const scheduled = (scheduleMap.get(techId) || []).sort((a, b) => a.start_time.localeCompare(b.start_time));
            
            // Get suggested time from smart-assign
            let startMin = new Date(techARanking.suggestedStartTime).getUTCHours() * 60 + new Date(techARanking.suggestedStartTime).getUTCMinutes();
            const durMin = calculateWallMinutes(option.partA.totalMinutes, meta.eff, meta.buf);
            
            // Push start past any overlapping existing ticket's optiFinish
            scheduled.forEach((t: any) => {
              const tStart = new Date(t.start_time);
              const tStartMin = tStart.getUTCHours() * 60 + tStart.getUTCMinutes();
              const tDur = calculateWallMinutes(t.predicted_minutes || 0, meta.eff, meta.buf);
              const tFinishMin = calculateTicketOptiFinish(tStartMin, tDur, meta.breaks);
              if (startMin >= tStartMin && startMin < tFinishMin + 1) {
                startMin = tFinishMin + 1;
              }
            });
            
            const finishMin = calculateTicketOptiFinish(startMin, durMin, meta.breaks);
            const day = new Date(dayStartISO);
            const startISO = new Date(day);
            startISO.setUTCHours(Math.floor(startMin / 60), startMin % 60, 0, 0);
            const finishISO = new Date(day);
            finishISO.setUTCHours(Math.floor(finishMin / 60), finishMin % 60, 0, 0);
            
            option.partA.scheduledStart = startISO.toISOString();
            option.partA.scheduledFinish = finishISO.toISOString();
            option.partA.proficiencyMatch = techARanking.proficiencyMatch;
          }
        } else if (part === 'B' && data?.rankings) {
          // Choose the suggested tech if provided; otherwise, fallback to top-ranked
          let techBRanking = null as any;
          if (option.partB.suggestedTechId) {
            techBRanking = data.rankings.find(
              (r: any) => r.technicianId === option.partB.suggestedTechId
            );
          }
          if (!techBRanking && data.rankings?.[0]) {
            techBRanking = data.rankings[0];
            option.partB.suggestedTechId = techBRanking.technicianId;
            option.partB.suggestedTechName = techBRanking.technicianName;
            option.partB.suggestedStartTime = techBRanking.suggestedStartTime;
          }
          if (techBRanking) {
            const techId = option.partB.suggestedTechId as string;
            const meta = techMeta.get(techId)!;
            const scheduled = (scheduleMap.get(techId) || []).sort((a, b) => a.start_time.localeCompare(b.start_time));
            
            // Get suggested time from smart-assign
            let startMin = new Date(techBRanking.suggestedStartTime).getUTCHours() * 60 + new Date(techBRanking.suggestedStartTime).getUTCMinutes();
            const durMin = calculateWallMinutes(option.partB.totalMinutes, meta.eff, meta.buf);
            
            // Push start past any overlapping existing ticket's optiFinish
            scheduled.forEach((t: any) => {
              const tStart = new Date(t.start_time);
              const tStartMin = tStart.getUTCHours() * 60 + tStart.getUTCMinutes();
              const tDur = calculateWallMinutes(t.predicted_minutes || 0, meta.eff, meta.buf);
              const tFinishMin = calculateTicketOptiFinish(tStartMin, tDur, meta.breaks);
              if (startMin >= tStartMin && startMin < tFinishMin + 1) {
                startMin = tFinishMin + 1;
              }
            });
            
            // CRITICAL FIX: For drop-off tickets WITHOUT time constraints, enforce sequential scheduling
            // Part B gets earliest slot, then Part A schedules AFTER Part B finishes
            if (!ticketData.startTime && !ticketData.dueOut) {
              const aFinish = option.partA.scheduledFinish ? new Date(option.partA.scheduledFinish) : null;
              if (aFinish) {
                const aFinishMin = aFinish.getUTCHours() * 60 + aFinish.getUTCMinutes();
                if (startMin < aFinishMin + 1) {
                  startMin = aFinishMin + 1;
                  console.log(`[PREVIEW-SEQUENCING] Part B must start after Part A finishes. Adjusted Part B start from ${startMin} to ${aFinishMin + 1} min`);
                }
              }
            } else {
              console.log(`[PREVIEW-SEQUENCING] Has time constraints (startTime=${ticketData.startTime}, dueOut=${ticketData.dueOut}). Independent scheduling allowed.`);
            }
            
            const finishMin = calculateTicketOptiFinish(startMin, durMin, meta.breaks);
            const day = new Date(dayStartISO);
            const startISO = new Date(day);
            startISO.setUTCHours(Math.floor(startMin / 60), startMin % 60, 0, 0);
            const finishISO = new Date(day);
            finishISO.setUTCHours(Math.floor(finishMin / 60), finishMin % 60, 0, 0);
            
            option.partB.scheduledStart = startISO.toISOString();
            option.partB.scheduledFinish = finishISO.toISOString();
            option.partB.proficiencyMatch = techBRanking.proficiencyMatch;
          }
        }
      });
      
      // Lock the earlier part and trigger cascade; then sequence the later part immediately after
      try {
        await Promise.all(options.map(async (option) => {
          if (!option.partA.scheduledStart || !option.partB.scheduledStart) return;
          const parts = [
            { key: 'A', part: option.partA },
            { key: 'B', part: option.partB },
          ].sort((a, b) => new Date(a.part.scheduledStart!).getTime() - new Date(b.part.scheduledStart!).getTime());
          const earlierPart = parts[0].part as any;
          const laterPart = parts[1].part as any;

          // Lock earlier part at its suggested start (reuses cascade path on backend)
          try {
            await supabase.functions.invoke('smart-assign', {
              body: {
                ticketData: {
                  shop_id: shopId,
                  type: 'Drop-off',
                  appointmentDate: format(selectedDate, 'yyyy-MM-dd'),
                  predictedMinutes: earlierPart.totalMinutes,
                  fixedStartTime: earlierPart.scheduledStart,
                  jobs: earlierPart.jobs.map((j: any) => ({
                    id: j.id,
                    name: j.name,
                    category: j.category,
                    proficiency_category: j.proficiency_category || j.category,
                    skill: j.skill,
                    minutes: j.minutes,
                    bay: j.bay,
                  })),
                },
                isScheduledTime: true,
                skipSplitCheck: true,
              },
            });
          } catch (e) {
            console.warn('[PREVIEW-LOCK] smart-assign lock failed for earlier part', e);
          }

          // Compute earlier finish in minutes and set later start to finish+1
          const earlyMeta = techMeta.get(earlierPart.suggestedTechId as string);
          const earlyDur = calculateWallMinutes(earlierPart.totalMinutes, earlyMeta?.eff || 100, resolvePlanningBufferPct(earlyMeta?.buf));
          const earlyStart = new Date(earlierPart.scheduledStart);
          const earlyStartMin = earlyStart.getUTCHours() * 60 + earlyStart.getUTCMinutes();
          const earlyFinishMin = calculateTicketOptiFinish(earlyStartMin, earlyDur, earlyMeta?.breaks || []);

          const day = new Date(dayStartISO);
          const laterStartISO = new Date(day);
          laterStartISO.setUTCHours(Math.floor((earlyFinishMin + 1) / 60), (earlyFinishMin + 1) % 60, 0, 0);
          laterPart.scheduledStart = laterStartISO.toISOString();

          const laterMeta = techMeta.get(laterPart.suggestedTechId as string);
          const laterDur = calculateWallMinutes(laterPart.totalMinutes, laterMeta?.eff || 100, resolvePlanningBufferPct(laterMeta?.buf));
          const laterFinishMin = calculateTicketOptiFinish(earlyFinishMin + 1, laterDur, laterMeta?.breaks || []);
          const laterFinishISO = new Date(day);
          laterFinishISO.setUTCHours(Math.floor(laterFinishMin / 60), laterFinishMin % 60, 0, 0);
          laterPart.scheduledFinish = laterFinishISO.toISOString();
        }));
      } catch (e) {
        console.warn('[PREVIEW-LOCK] sequencing adjustments failed', e);
      }

      // Validate due-out times if provided
      if (ticketData.dueOut) {
        const dueOutDate = new Date(ticketData.dueOut);
        options.forEach((option) => {
          const aFinish = option.partA.scheduledFinish ? new Date(option.partA.scheduledFinish) : null;
          const bFinish = option.partB.scheduledFinish ? new Date(option.partB.scheduledFinish) : null;
          
          if (aFinish && aFinish > dueOutDate) {
            option.partA.dueOutViolation = true;
          }
          if (bFinish && bFinish > dueOutDate) {
            option.partB.dueOutViolation = true;
          }
        });
      }
      
      
      setSplitOptions(options);
      setShowSplitPreview(true);
    } catch (error: any) {
      toast({
        title: 'Error analyzing split options',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setIsAnalyzing(false);
    }
  };

  // Handle view next split option
  const handleViewNextOption = () => {
    setCurrentSplitOptionIndex((prev) => (prev + 1) % splitOptions.length);
  };
  
  // Handle confirm split from preview dialog
  /**
   * 8G.2D.2D §7 — ONE CONFIRMED STRUCTURAL PROPOSAL = ONE OPERATION ID.
   *
   * Keyed on the canonical content of the decision (which real job lines go to
   * which part, and to whom), so a transport retry of the SAME confirmed split
   * reuses the key and the database answers ALREADY_APPLIED with the family it
   * already created. Choosing a materially different option mints a new key; a
   * rerender changes neither. Deliberately NOT keyed on the synthetic ids the
   * unpersisted path uses — those are random per render.
   */
  const handleConfirmSplit = async (optionIndex: number) => {
    const option = splitOptions[optionIndex];
    setIsSplitting(true);
    
    // R40 FIX-5: Compensating rollback accumulator for partial-failure cleanup
    
    try {
      // Use activeShop from context for proper RLS compliance
      const shopId = activeShop?.id;

      // ══ 8G.2D.2D — PERSISTED STRUCTURAL SPLIT ═══════════════════════════
      // Branched HERE, before any write model is chosen, so the creation path
      // and the structural path cannot blend. Everything below is the
      // UNPERSISTED creation flow: no source to supersede, no lines to move,
      // so minting jobs there is correct.
      //
      // For a persisted source the consequential act belongs entirely to
      // `execute_structural_split_atomic`. What this replaces cloned the work
      // into brand-new `jobs` rows, cancelled rollover descendants to make
      // itself eligible, and tombstoned the source with the technician-blind
      // primitive.
      if (ticketData.id) {
        const partJobIds = (part: { jobs: Array<{ id: string }> }) =>
          part.jobs.map((j) => j.id);

        // ══ 8G.2D.2D.1 §7 — THE CONFIRMED SCHEDULE IS PART OF THE ACT ══════
        // The preview shows the operator when each part starts. Those times
        // were computed and displayed and then dropped on the floor: the
        // authority accepts `start_time`, and omitting it let each child fall
        // back to the source's old start. The operator confirmed a schedule;
        // this sends the schedule they confirmed.
        const parts = [
          {
            job_ids: partJobIds(option.partA),
            tech_id: option.partA.suggestedTechId ?? null,
            start_time: option.partA.scheduledStart ?? null,
            predicted_minutes: option.partA.totalMinutes,
          },
          {
            job_ids: partJobIds(option.partB),
            tech_id: option.partB.suggestedTechId ?? null,
            start_time: option.partB.scheduledStart ?? null,
            predicted_minutes: option.partB.totalMinutes,
          },
        ];

        // ══ §8 — THE KEY COVERS THE WHOLE DECISION ════════════════════════
        // Built from the payload actually sent. The previous key held only
        // source + job ids + tech ids, so moving a part's start or changing
        // its duration reused the same operation id — and the authority would
        // have answered ALREADY_APPLIED to a materially different proposal.
        // DERIVED, not stored. A `useRef` survives a rerender and nothing
        // else: after a refresh the old key was gone, a retry minted a new id,
        // and the authority answered SOURCE_NOT_SPLITTABLE — telling the
        // operator their ticket could not be split when their own split was
        // the reason. Deriving it makes the same decision yield the same id in
        // any tab, so the authority can answer ALREADY_APPLIED instead.
        const operationId = await deriveSplitOperationId(ticketData.id, parts);

        // The user's own authenticated client. The authority derives its actor
        // from auth.uid() and its shop from the source row; nothing here may
        // name either. `(supabase as any).rpc` matches the established
        // convention for authorities absent from the generated types — see
        // ticketTerminalLifecycle / completedPickupLifecycle.
        const { data: splitResult, error: splitError } = await (supabase as any).rpc(
          'execute_structural_split_atomic',
          {
            p_source_ticket_id: ticketData.id,
            p_operation_id: operationId,
            p_parts: parts,
            p_split_reason: 'SKILL_SPLIT',
          },
        );

        if (splitError) {
          toast({ variant: 'destructive', title: 'Split failed', description: splitError.message });
          setIsSplitting(false);
          return;
        }

        const outcome = (splitResult as any)?.outcome as string | undefined;
        if (outcome !== 'SPLIT_APPLIED' && outcome !== 'ALREADY_APPLIED') {
          // A canonical refusal. Nothing was written. Surfaced as the reason it
          // actually is — never translated into generic failure, and never
          // "resolved" by cancelling whatever blocked it.
          const REFUSAL_COPY: Record<string, string> = {
            ACTIVE_WORK: 'A technician is working on this ticket. Stop the timer first.',
            PAUSED_WORK: 'A technician is paused on this ticket. Resolve the pause first.',
            REVIEW_REQUIRED: 'Measured time on this ticket is awaiting review. Accept the review first.',
            TIMER_INTEGRITY_BLOCK: 'This ticket has a timer integrity conflict that a person must resolve.',
            ACTIVE_ROLLOVER_DESCENDANTS: 'This ticket has an active rollover continuation. Resolve the rollover chain first.',
            SOURCE_IS_ROLLOVER_DESCENDANT: 'This ticket is itself a rollover continuation and cannot be split.',
            OPEN_JOB_NOT_TRANSFERABLE: 'Work on this ticket has already been measured, so it belongs to this visit.',
            TERMINAL_JOB_NOT_TRANSFERABLE: 'Completed work belongs to this visit and cannot move to a new ticket.',
            OPEN_JOB_NOT_COVERED: 'Every remaining job must be assigned to one of the parts.',
            SOURCE_NOT_SPLITTABLE: 'This ticket can no longer be split.',
            TICKET_NOT_AVAILABLE: 'This ticket is not available to you.',
            OPERATION_ID_CONFLICT: 'That split attempt is already recorded against a different ticket.',
            CHILD_ID_CONFLICT: 'A ticket already exists with one of the new part ids.',
            FOREIGN_TECHNICIAN: 'That technician does not belong to this shop.',
            INVALID_PROPOSAL: 'This split proposal is not valid.',
            UNKNOWN_SOURCE_JOB: 'This proposal references work the ticket does not own.',
          };
          toast({ variant: 'destructive', title: 'Split not possible',
                  description: REFUSAL_COPY[outcome ?? ''] ?? (splitResult as any)?.note ?? outcome });
          setIsSplitting(false);
          return;
        }

        // Derived, strictly after the transaction committed. A retry that
        // answered ALREADY_APPLIED is not a second human decision.
        const children = ((splitResult as any)?.children ?? []) as Array<{ id: string }>;
        toast(outcome === 'SPLIT_APPLIED'
          ? { title: 'Ticket split', description: `Split into ${children.length} parts.` }
          : { title: 'Already split', description: 'This split was already applied.' });

        setIsSplitting(false);
        onClose();
        if (children[0]) onAssignmentComplete?.(children[0].id);
        return;
      }
      
      if (!shopId) {
        toast({
          variant: 'destructive',
          title: 'Error',
          description: 'No active shop selected',
        });
        setIsSplitting(false);
        return;
      }

      // R39 FIX-4: Get current user for audit trail
      const { data: { user: splitUser } } = await supabase.auth.getUser();
      const splitUserId = splitUser?.id || null;

      console.log('[SPLIT] Step 1: Creating actual jobs in database...');

      // STEP 1: Create all jobs in the jobs table and get real UUIDs.
      //
      // OHF-A3: batched job creation for split-confirm. Pre-OHF-A3 the
      // per-job for-loop fired one `jobs.insert(...).select().single()`
      // per iteration over `[...partA.jobs, ...partB.jobs]` — for a
      // 2+3 split that's 5 sequential round trips before the first
      // ticket stub is created. Mirrors the OHF-A2 batched pattern used
      // by `handleCreateTicket` (and `optiqueue-confirm:728-752` post
      // OPS-A5) — Supabase's `.insert(array).select()` returns rows in
      // INSERT order on a non-distributed Postgres single-statement
      // call, which we leverage for index-correlated `jobIdMap`
      // population. The defensive row-count guard catches any contract
      // drift fail-fast at the correlation point rather than letting
      // a mismatched id slip into downstream `ticket_jobs` linking
      // (consumers at L~1092 + L~1176 read `jobIdMap.get(job.id)!`,
      // which would silently get the wrong UUID under miscorrelation).
      // Both are now built per PART instead of over a combined array, because
      // each bundle group owns its own jobs. `jobIdMap` is declared below, after
      // the transaction returns the committed ids.

      // ══════════════════════════════════════════════════════════════════
      // ATOMIC SPLIT BUNDLE
      // ══════════════════════════════════════════════════════════════════
      // Pre-atomic this issued FIVE independent statements — one batched
      // jobs.insert, then tickets.insert + ticket_jobs.insert for Part A, then
      // the same pair for Part B — each guarded by a compensating DELETE pushed
      // onto `rollbackActions`. That is not atomicity: the compensating delete
      // can itself fail, Realtime consumers already observed the inserts, and a
      // Part B failure left Part A committed. Which is the same orphan class
      // that shipped to production on 2026-08-07, one level up.
      //
      // Both parts now go through ONE `create_ticket_bundle` call as two ticket
      // GROUPS. Each group carries its own jobs and its own ticket_jobs, and the
      // RPC injects ticket_id / job_id from that group's inserted rows — so a
      // link can never reference another group's ticket, nor an id that failed
      // to commit. Any failure anywhere rolls back both parts entirely.
      //
      // OHF-A3's batching intent is preserved: job payloads are still built by a
      // single `.map()` per part with no per-job round trip. What changed is
      // delivery — five statements become one, which is strictly fewer round
      // trips AND transactional.

      // Generate unique IDs for split tickets
      const splitGroupId = crypto.randomUUID();

      // Create vehicle first if needed. This is deliberately OUTSIDE the bundle:
      // a vehicle is not part of the split's required ticket state, it is a
      // pre-existing entity the tickets reference, and it is reused rather than
      // recreated on retry.
      let vehicleId = ticketData.vehicleId || null;

      if (!vehicleId && ticketData.vehicle) {
        const newVehicleId = await createVehicle({
          shop_id: shopId,
          make: ticketData.vehicle.make || '',
          model: ticketData.vehicle.model || '',
          year: ticketData.vehicle.year,
          engine_size: ticketData.vehicle.engineSize || '',
          vin: ticketData.vehicle.vin || '',
          license_plate: ticketData.vehicle.licensePlate || '',
        });
        vehicleId = newVehicleId;
      }

      // R33: Make the smart-assign-failed fallback observable. The bare
      // `option.partX.scheduledStart || new Date().toISOString()` silently
      // defaulted to "now" when smart-assign returned no scheduledStart for the
      // split candidate — operator saw no indication that the placement was a
      // fallback rather than the optimizer's recommendation.
      //
      // Both warnings now fire BEFORE any write rather than Part B's firing
      // after Part A had already been inserted. That is the only ordering change
      // here, and it is the correct one: nothing should be persisted before the
      // operator has seen every placement caveat.
      if (!option.partA.scheduledStart) {
        console.warn('[SPLIT] Part A scheduledStart missing from optimizer ranking — falling back to current time. Tech may need manual placement.');
        toast({
          title: 'Part A placement defaulted',
          description: 'The optimizer did not return a recommended start time for Part A — defaulted to now. Verify placement before confirming.',
          variant: 'destructive',
        });
      }
      const partAStartTime = option.partA.scheduledStart || clock.now().toISOString();

      // R33: Same observable-fallback pattern as Part A above.
      if (!option.partB.scheduledStart) {
        console.warn('[SPLIT] Part B scheduledStart missing from optimizer ranking — falling back to current time. Tech may need manual placement.');
        toast({
          title: 'Part B placement defaulted',
          description: 'The optimizer did not return a recommended start time for Part B — defaulted to now. Verify placement before confirming.',
          variant: 'destructive',
        });
      }
      const partBStartTime = option.partB.scheduledStart || clock.now().toISOString();

      const partAId = `${splitGroupId}-A`;
      const partBId = `${splitGroupId}-B`;

      // Shared ticket columns. Extracted so Part A and Part B cannot drift
      // apart on fields that are by definition identical for both halves of
      // one split.
      const splitScheduledStartTime =
        ticketData.type === 'Waiter' && ticketData.startTime
          ? convertDatetimeLocalToUTC(`${format(selectedDate, 'yyyy-MM-dd')}T${ticketData.startTime}`, shopTimezone)
          : null;

      const splitTicketCommon = {
        simple_ticket_number: 0, // Placeholder - trigger will set real value
        shop_id: shopId,
        vehicle_id: vehicleId,
        customer_name: ticketData.customerName || '',
        customer_phone: ticketData.customerPhone || '',
        type: ticketData.type,
        due_out: ticketData.dueOut || null,
        is_comeback: ticketData.isComeback || false,
        priority: ticketData.priority || 'Normal',
        status: 'Active',
        split_group_id: splitGroupId,
        split_reason: option.type,
        scheduled_start_time: splitScheduledStartTime,
        // R39 FIX-1 + FIX-4: Add scheduled_date, audit, and data completeness fields
        scheduled_date: selectedDayLabel,
        created_by: splitUserId,
        parking_lot: false,
        customer_id: ticketData.customerId || null,
        // Core-100 R4 CORE-Y: a split chosen while CREATING a ticket records the creation's origin on both parts
        // (a split of an existing ticket is a structural change — the database derives that from its lineage).
        ...(ticketData.id ? {} : { creation_method: ticketData.creationMethod ?? 'create_ticket' }),
      };
      // Core-100 R5 (CORE-AQ): only Part A carries the call proposal (one ticket per proposal).
      const partACallLineage = !ticketData.id && ticketData.sourceCallProposalId
        ? { source_call_proposal_id: ticketData.sourceCallProposalId }
        : {};

      const partATicket = {
        ...splitTicketCommon,
        ...partACallLineage,
        id: partAId,
        assigned_tech_id: option.partA.suggestedTechId,
        // CORE-AB-GAP-008 / D3 — an INSERT establishes the plan (the sync trigger copies scheduled_tech_id from assigned_tech_id).
        dispatch_assignment_source: option.partA.suggestedTechId ? 'manual' : null,
        original_tech_id: option.partA.suggestedTechId, // PHASE 1: Set original tech on split tickets
        start_time: partAStartTime,
        predicted_minutes: option.partA.totalMinutes,
        split_sequence: 1,
      };

      const partBTicket = {
        ...splitTicketCommon,
        id: partBId,
        assigned_tech_id: option.partB.suggestedTechId,
        // CORE-AB-GAP-008 / D3 — an INSERT establishes the plan (the sync trigger copies scheduled_tech_id from assigned_tech_id).
        dispatch_assignment_source: option.partB.suggestedTechId ? 'manual' : null,
        original_tech_id: option.partB.suggestedTechId, // PHASE 1: Set original tech on split tickets
        start_time: partBStartTime,
        predicted_minutes: option.partB.totalMinutes,
        split_sequence: 2,
      };

      // Job payloads per part. Same columns and coercions the single batched
      // insert used; only the grouping changed, so each part owns its own jobs.
      const splitJobPayload = (job: any) => ({
        name: job.name,
        category: job.category || '',
        submenu: job.submenu || '',
        bay_requirement: job.bay || 'Lift',
        proficiency_category: job.proficiency_category || '',
        skill_level: job.skill,
        standard_minutes: job.minutes,
        shop_id: shopId,
      });

      // ticket_jobs rows per part. `ticket_id` / `job_id` are deliberately
      // ABSENT — the RPC injects both from this group's inserted rows.
      const buildSplitTicketJobs = (
        jobs: any[],
        technicianId: string | null,
      ) => Promise.all(jobs.map(async (job) => {
        const laborCols = await buildLaborColumnsForAutoWrite({
          shopId,
          effectiveMinutes: job.minutes,
          jobCategory: (job as any).category ?? null,
          flags: { labor_engine_enabled: laborEngineEnabled, ui_ticket_financials: ticketFinancialsEnabled },
        });
        const techPayCols = await buildTechPayColumnsForAutoWrite({
          shopId,
          technicianId: technicianId ?? null,
          referenceMinutes: job.minutes,
          retailSoldMinutes: (laborCols as any)?.sold_minutes ?? null,
          flags: { labor_engine_enabled: laborEngineEnabled, ui_technician_pay: techPayEnabled },
        });
        return {
          minutes_override: job.minutes,
          ...(laborCols ?? {}),
          ...(techPayCols ?? {}),
        };
      }));

      const [partATicketJobs, partBTicketJobs] = await Promise.all([
        buildSplitTicketJobs(option.partA.jobs, option.partA.suggestedTechId ?? null),
        buildSplitTicketJobs(option.partB.jobs, option.partB.suggestedTechId ?? null),
      ]);

      console.log('[SPLIT] Creating both parts atomically', {
        splitGroupId,
        partAId,
        partBId,
        partAJobs: option.partA.jobs.length,
        partBJobs: option.partB.jobs.length,
      });

      // ── THE single atomic write for the whole split ────────────────────
      const splitBundle = await createTicketBundle([
        {
          ticket: partATicket,
          jobs: option.partA.jobs.map(splitJobPayload),
          ticketJobs: partATicketJobs,
        },
        {
          ticket: partBTicket,
          jobs: option.partB.jobs.map(splitJobPayload),
          ticketJobs: partBTicketJobs,
        },
      ]);

      // Past this line BOTH parts, all their jobs and all their links are
      // committed. Nothing below may assume it can undo them, and nothing above
      // may have announced them.

      // temp job id -> real job id, preserved from the pre-atomic
      // `jobIdMap` semantic. Ids now come from the transaction's return value in
      // per-group input order (createTicketBundle already rejected a count
      // mismatch), instead of from a client insert response.
      const jobIdMap = new Map<string, string>();
      option.partA.jobs.forEach((job, idx) => {
        jobIdMap.set(job.id, splitBundle[0].jobIds[idx]);
      });
      option.partB.jobs.forEach((job, idx) => {
        jobIdMap.set(job.id, splitBundle[1].jobIds[idx]);
      });
      console.log(`[SPLIT] Created ${jobIdMap.size} jobs across 2 parts in one transaction`);

      console.log('[SPLIT] Step 4: Calling smart-assign for Part A (will check existing tickets)...');
      
      // Determine Part A's start time - TIMEZONE FIX
      // If neither has scheduledStartTime/dueOut, Part A should start after Part B finishes
      let partAFixedStart = option.partA.scheduledStart;
      if (!ticketData.startTime && !ticketData.dueOut && option.partB.scheduledFinish) {
        // Import timezone utilities
        const { toZonedTime, fromZonedTime } = await import('date-fns-tz');
        
        // Get shop timezone from context or hours
        const shopTZ = shopTimezone || (shopHours as any)?.timezone || (shopHours as any)?.time_zone || (shopHours as any)?.timeZone || 'America/Chicago';
        
        // Parse Part B finish time in SHOP timezone (not browser local time)
        const bFinishDate = toZonedTime(new Date(option.partB.scheduledFinish), shopTZ);
        const bFinishMin = bFinishDate.getHours() * 60 + bFinishDate.getMinutes();
        
        // Parse Part A start time in SHOP timezone
        const aStartDate = toZonedTime(new Date(option.partA.scheduledStart || clock.now()), shopTZ);
        const aStartMin = aStartDate.getHours() * 60 + aStartDate.getMinutes();
        
        console.log(`[TIMEZONE-FIX] Part B finish: ${bFinishMin} min (${bFinishDate.toLocaleTimeString()}), Part A current start: ${aStartMin} min (${aStartDate.toLocaleTimeString()}) in ${shopTZ}`);
        
        if (aStartMin < bFinishMin + 1) {
          // Adjust Part A to start 1 minute after Part B finishes (in shop timezone)
          const adjustedStartZoned = new Date(bFinishDate);
          adjustedStartZoned.setHours(Math.floor((bFinishMin + 1) / 60), (bFinishMin + 1) % 60, 0, 0);
          
          // Convert back to UTC for storage
          const adjustedStartUTC = fromZonedTime(adjustedStartZoned, shopTZ);
          partAFixedStart = adjustedStartUTC.toISOString();
          
          console.log(`[TIMEZONE-FIX] Adjusted Part A start from ${option.partA.scheduledStart} to ${partAFixedStart} (shop time: ${adjustedStartZoned.toLocaleTimeString()})`);
        }
      }
      
      // STEP 4: Call smart-assign for Part A to get 100% validated times
      // smart-assign will automatically fetch all existing tickets for the technician and find non-overlapping times
      const { data: assignResultA, error: assignErrorA } = await supabase.functions.invoke('smart-assign', {
        body: {
          ticketData: {
            shop_id: shopId,
            type: ticketData.type,
            appointmentDate: format(selectedDate, 'yyyy-MM-dd'),
            predictedMinutes: option.partA.totalMinutes,
            fixedStartTime: partAFixedStart || null,
            jobs: option.partA.jobs.map(j => ({
              id: j.id,
              name: j.name,
              category: j.category,
              category_key: j.category, // CRITICAL: category is already the category_key from CreateTicket
              proficiency_category: j.proficiency_category || getProficiencyCategory(j.category),
              skill: j.skill,
              minutes: j.minutes,
              bay: j.bay,
            })),
          },
          isScheduledTime: ticketData.type === 'Waiter' && !!ticketData.startTime,
          skipSplitCheck: true,
        },
      });

      if (assignErrorA) {
        console.error('[SPLIT] smart-assign error for Part A:', assignErrorA);
        throw assignErrorA;
      }

      // Update Part A with validated times from smart-assign (use chosen tech ranking)
      let validatedStartA: string | null = null;
      let validatedFinishA: string | null = null;
      if (assignResultA?.rankings && Array.isArray(assignResultA.rankings)) {
        const r = assignResultA.rankings.find((rk: any) => rk.technicianId === option.partA.suggestedTechId);
        validatedStartA = r?.suggestedStartTime || null;
        validatedFinishA = r?.suggestedFinishTime || null;
      }
      if (validatedStartA) {
        await supabase
          .from('tickets')
          .update({
            start_time: validatedStartA,
            assigned_tech_id: option.partA.suggestedTechId,
          })
          .eq('id', partAId);
        console.log('[SPLIT] Part A scheduled at:', validatedStartA, 'finishes at:', validatedFinishA);
      } else {
        console.warn('[SPLIT] No ranking start time returned for Part A; leaving temporary start_time');
      }

      console.log('[SPLIT] Step 5: Waiting for Part A to be committed, then calling smart-assign for Part B...');
      
      // CRITICAL: Wait a moment for Part A to be fully committed to the database
      // This ensures smart-assign will see Part A when calculating Part B's placement
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // STEP 6: Call smart-assign for Part B - it will now see Part A and avoid overlap
      const { data: assignResultB, error: assignErrorB } = await supabase.functions.invoke('smart-assign', {
        body: {
          ticketData: {
            shop_id: shopId,
            type: ticketData.type,
            appointmentDate: format(selectedDate, 'yyyy-MM-dd'),
            predictedMinutes: option.partB.totalMinutes,
            fixedStartTime: option.partB.scheduledStart || null,
            jobs: option.partB.jobs.map(j => ({
              id: j.id,
              name: j.name,
              category: j.category,
              category_key: j.category, // CRITICAL: category is already the category_key from CreateTicket
              proficiency_category: j.proficiency_category || getProficiencyCategory(j.category),
              skill: j.skill,
              minutes: j.minutes,
              bay: j.bay,
            })),
          },
          isScheduledTime: false,
          skipSplitCheck: true,
        },
      });

      if (assignErrorB) {
        console.error('[SPLIT] smart-assign error for Part B:', assignErrorB);
        throw assignErrorB;
      }

      // Update Part B with validated times from smart-assign (use chosen tech ranking)
      let validatedStartB: string | null = null;
      if (assignResultB?.rankings && Array.isArray(assignResultB.rankings)) {
        const r = assignResultB.rankings.find((rk: any) => rk.technicianId === option.partB.suggestedTechId);
        validatedStartB = r?.suggestedStartTime || null;
      }
      if (validatedStartB) {
        await supabase
          .from('tickets')
          .update({
            start_time: validatedStartB,
            assigned_tech_id: option.partB.suggestedTechId,
          })
          .eq('id', partBId);
        console.log('[SPLIT] Part B scheduled at:', validatedStartB);
      } else {
        console.warn('[SPLIT] No ranking start time returned for Part B; leaving temporary start_time');
      }

      // STEP 7: Cancel rollover descendants + mark original as Deleted (R49 FIX-1)
      // ══ 8G.2D.2D — LEGACY STRUCTURAL BLOCK REMOVED ══════════════════════
      //
      // What stood here ran only when `ticketData.id` was set — a PERSISTED
      // source — and did three things this phase retires:
      //
      //   1. queried active rollover descendants and cancelled them one by one
      //      via cancelTicketChain so Split could become eligible. The
      //      authority's answer is ACTIVE_ROLLOVER_DESCENDANTS with zero
      //      writes; making yourself eligible by cancelling what blocked you is
      //      not a split, it is two acts wearing one button.
      //   2. tombstoned the source through
      //      `softDeleteTicketWithSessionClose`, which force-closes every open
      //      timer session on the ticket — technician-blind.
      //   3. emitted a source status event after those writes.
      //
      // The persisted path now returns far above this point, inside one
      // database transaction. Everything from here on is the UNPERSISTED
      // creation flow, which has no source to supersede.

      // Codex: Trigger cascade for each affected technician after split (Round 37, FIX 5)
      const { triggerBoardCascade } = await import('@/utils/cascadeTrigger');
      const affectedTechs = new Set<string>();
      if (option.partA.suggestedTechId) affectedTechs.add(option.partA.suggestedTechId);
      if (option.partB.suggestedTechId) affectedTechs.add(option.partB.suggestedTechId);
      // FIX-A4 (R57): Await cascades instead of fire-and-forget for observability
      for (const techId of affectedTechs) {
        try {
          await triggerBoardCascade(activeShop!.id, selectedDayLabel, techId);
        } catch (err) {
          console.warn('[SPLIT] Post-split cascade error:', err);
          toast({ title: '⚠️ Schedule may need refresh', description: 'Automatic recalculation failed.' });
        }
      }

      toast({
        title: 'Ticket Split Successfully',
        description: `Created 2 tickets with 100% validated scheduling: Part A (${option.partA.jobs.length} jobs) and Part B (${option.partB.jobs.length} jobs)`,
      });

      // Cross-device Dayboard sync after split completion
      void notifyDayboardInvalidated({
        reason: 'optimizer-reassign',
        shopId: activeShop!.id,
        ticketId: partAId,
        scheduledDate: selectedDayLabel,
        metadata: { kind: 'optiplace-split' },
      });

      onAssignmentComplete(partAId);
      onClose();
    } catch (error: any) {
      console.error('[SPLIT] Error:', error);
      // R40 FIX-5's compensating rollback is GONE, and deliberately so. It
      // existed to delete required rows that had already committed when a later
      // required insert failed — jobs, Part A's ticket and links, Part B's
      // ticket and links. `create_ticket_bundle` now commits all of them in one
      // transaction, so there is nothing left to compensate for: a failure here
      // means nothing was written. Keeping a no-op rollback loop would imply the
      // required writes are still separable, which is exactly the belief that
      // produced the 2026-08-07 orphan.
      const callRefusal = callProposalRefusalMessage(error);
      toast({
        title: callRefusal ? 'Call suggestion refused' : 'Split Failed',
        description: callRefusal ?? (error.message || 'Failed to split ticket'),
        variant: 'destructive',
      });
      if (callRefusal) { onCallProposalRefused?.(callRefusal); onClose(); }
    } finally {
      setIsSplitting(false);
    }
  };

  // wallToBook deleted — use canonical wallToBookMinutes from canonicalTimeMath.ts

  const createTicketWithAssignment = async () => {
    if (!selectedTechId) {
      toast({
        title: 'No Selection',
        description: 'Please select a technician',
        variant: 'destructive',
      });
      return;
    }

    breadcrumbs.log('OB:GUARD', { action: 'optiplace_create_start', selectedTechId });

    setIsCreating(true);
    
    // Check if this is a reassignment (existing ticket)
    const isReassignment = !!ticketData.id;
    
    if (isReassignment) {
      // Handle reassignment of existing ticket using the reassign-ticket edge function
      try {
        const chosenRanking = rankings.find(r => r.technicianId === selectedTechId);
        
        if (!chosenRanking) {
          throw new Error('Selected technician not found in rankings');
        }
        
        console.log('[REASSIGNMENT] Calling reassign-ticket edge function', {
          ticketId: ticketData.id,
          targetTechId: selectedTechId,
          scheduledDate: format(selectedDate, 'yyyy-MM-dd'),
        });
        
        // OD-40 (founder, 2026-10-15): the advisor accepted what this screen SHOWED — the technician, the date and
        // the start time. Without desiredStartTime the server appended the ticket on its original day (kebab mode),
        // so the placement could differ from the recommendation. The shown start is sent; the server still checks
        // it (past time, earliest feasible start, the technician's other work, CAS) and never double-books.
        const shownStart = ((chosenRanking.rolloverParts?.length ?? 0) > 1 ? chosenRanking.rolloverParts![0].startTime : null)
          || chosenRanking.suggestedStartTime || undefined;
        // Use the reassign-ticket edge function to properly reassign the ENTIRE ticket
        const { data: reassignData, error: reassignError } = await supabase.functions.invoke('reassign-ticket', {
          body: {
            ticketId: ticketData.id,
            targetTechId: selectedTechId,
            scheduledDate: format(selectedDate, 'yyyy-MM-dd'),
            ...(shownStart ? { desiredStartTime: shownStart } : {}),
            manualOverride: false, // OptiPlace uses auto-logic, not manual override
            // CORE-AB-GAP-008 / D3 — the dispatcher picked this technician
            // from the ranked list on this screen. The server classifies the
            // origin from this reason; it is not free text.
            moveReason: 'optiplace-reassign',
          },
        });

        if (reassignError) throw reassignError;

        console.log('[REASSIGNMENT] Success:', reassignData);

        // Ranking audit, as on the create path: the server's ranking run is attached to the ticket with the
        // technician the person chose (OD-40 imported-ticket review uses this path). Not awaited; failures reported.
        void recordAssignmentSelection({
          rankingRecord: rankingRecordRef.current,
          ticketId: ticketData.id,
          selectedTechId: selectedTechId ?? null,
          shopId: activeShop?.id ?? '',
          source: 'optiplace',
        });

        const cascadedCount = reassignData?.cascadedCount || 0;
        const cascadeMsg = cascadedCount > 0 ? ` (cascaded ${cascadedCount} tickets)` : '';

        toast({
          title: 'Ticket Reassigned',
          description: `Successfully reassigned to ${chosenRanking.technicianName}${cascadeMsg}`,
        });

        onAssignmentComplete(ticketData.id);
        onClose();
      } catch (error: any) {
        console.error('[REASSIGNMENT] Error:', error);
        toast({
          title: 'Reassignment Failed',
          description: error.message || 'Failed to reassign ticket',
          variant: 'destructive',
        });
      } finally {
        setIsCreating(false);
      }
      return;
    }
    
    // Original create logic for new tickets
    try {
      // Use activeShop from context for proper RLS compliance
      const shopId = activeShop?.id;
      
      if (!shopId) {
        toast({
          variant: 'destructive',
          title: 'Error',
          description: 'No active shop selected',
        });
        setIsCreating(false);
        return;
      }

      // Fetch technician efficiency and buffer percentages for conversion.
      // DMC-G2: dropped unused `shift_start` from the projection — the
      // downstream code reads only efficiency_percentage + buffer_percentage.
      const { data: techData, error: techError } = await supabase
        .from('technicians')
        .select('efficiency_percentage, buffer_percentage')
        .eq('id', selectedTechId)
        .single();

      if (techError || !techData) {
        toast({
          variant: 'destructive',
          title: 'Error',
          description: 'Could not load technician data',
        });
        setIsCreating(false);
        return;
      }

      const techEff = techData.efficiency_percentage || 100;
      const techBuffer = resolvePlanningBufferPct(techData.buffer_percentage);

      // Get or create vehicle.
      //
      // K19-2 (data integrity): an existing selected `vehicleId` ALWAYS wins —
      // reuse it, never create a duplicate. `CreateTicket` passes both a
      // prefilled `vehicle` object (with a year) AND `vehicleId` when a returning
      // customer's existing vehicle is chosen, so the old `if (vehicle.year)`
      // branch created a brand-new vehicle and orphaned the selected one on every
      // returning-customer ticket. `decideTicketVehicleAction` enforces the same
      // precedence the split-ticket path already uses (vehicleId first, create
      // only when absent).
      // The durable Create Ticket operation, while it is unfinished (cleared on success).
      const durableOperation = ticketData.createOperationId ? readCreateOperation(ticketData.createOperationId) : null;
      let vehicleData;
      const vehicleAction = decideTicketVehicleAction({
        vehicleId: ticketData.vehicleId,
        vehicle: ticketData.vehicle,
      });
      if (vehicleAction.action === 'reuse') {
        // Returning customer / reassignment: validate + reuse the existing
        // vehicle. K19-2 hardening: scope the lookup to the CURRENT shop as well
        // as the id, so a stale/wrong/cross-shop `vehicleId` fails closed (the
        // `.single()` throws on no match) rather than linking the new ticket to
        // another tenant's vehicle.
        const { data: existingVehicle, error: vehicleError } = await supabase
          .from('vehicles')
          .select('id')
          .eq('id', vehicleAction.vehicleId)
          .eq('shop_id', shopId)
          .single();

        if (vehicleError) throw vehicleError;
        vehicleData = existingVehicle;
      } else if (vehicleAction.action === 'create' && (createAttemptRef.current?.vehicleId || durableOperation?.vehicleId)) {
        // A retry of this same operation: the vehicle the first attempt created.
        vehicleData = { id: (createAttemptRef.current?.vehicleId || durableOperation?.vehicleId)! };
      } else if (vehicleAction.action === 'create') {
        // New customer / new vehicle: create it (encrypted vehicle-write path).
        const newVehicleId = await createVehicle({
          shop_id: shopId,
          year: ticketData.vehicle.year,
          make: ticketData.vehicle.make,
          model: ticketData.vehicle.model,
          engine_size: ticketData.vehicle.engineSize || null,
          vin: ticketData.vehicle.vin || null,
          license_plate: ticketData.vehicle.licensePlate || null,
          customer_id: ticketData.customerId || null,
          gvwr_lbs: ticketData.gvwrLbs || null,
        });
        vehicleData = { id: newVehicleId };
      } else {
        throw new Error('No vehicle information provided for ticket creation');
      }

      // The ticket id belongs to the Create Ticket OPERATION, not to this click
      // and not to this opening of OptiPlace: a durable operation
      // (createTicketOperation.ts) when the caller supplies one, otherwise this
      // opening's attempt. A retry reuses it.
      if (!createAttemptRef.current) {
        createAttemptRef.current = durableOperation
          ? { ticketId: durableOperation.id, vehicleId: durableOperation.vehicleId, chainNowMs: durableOperation.chainNowMs, submitted: true }
          : { ticketId: crypto.randomUUID(), vehicleId: null, chainNowMs: clock.nowMs(), submitted: false };
      }
      const attempt = createAttemptRef.current;
      if (vehicleAction.action === 'create') {
        attempt.vehicleId = vehicleData.id;
        if (durableOperation) rememberCreateOperationVehicle(durableOperation.id, vehicleData.id);
      }
      const ticketId = attempt.ticketId;

      // Get current user for created_by field
      const { data: { user } } = await supabase.auth.getUser();
      const currentUserId = user?.id || null;

      // Determine start time to use.
      //
      // Fallback order (M1 fix — matches the Catalyst auto-assign path):
      //   1. chosenRanking?.suggestedStartTime  — smart-assign UTC ISO (preferred)
      //   2. waiterFixed                        — Waiter exact-time anchor (preserved)
      //   3. getTodaySafeStartTime(...)         — shop-tz shift start, clamped to
      //                                           now+1min only when selectedDay
      //                                           is today (was: selectedDate.toISOString(),
      //                                           which is browser-local noon and
      //                                           drifts a day for cross-TZ shops)
      //
      // The default shift start '08:00' mirrors useCatalystAutoAssign.ts:153
      // and CreateTicket's 08:00 appointment fallback.
      const chosenRanking = rankings.find(r => r.technicianId === selectedTechId);
      const waiterFixed = String(ticketData.type || '').toLowerCase() === 'waiter' && ticketData.startTime
        ? `${format(selectedDate, 'yyyy-MM-dd')}T${ticketData.startTime}`
        : null;
      const safeFallback = getTodaySafeStartTime('08:00', selectedDayLabel, shopTimezone);
      // A rollover plan's Part A start comes first: a ranked rollover
      // candidate reports `suggestedStartTime: null`, and the 08:00 fallback
      // would write Part A's minutes behind a start they were not sized for.
      const rankedRolloverStart = (chosenRanking?.rolloverParts?.length ?? 0) > 1
        ? chosenRanking!.rolloverParts![0].startTime
        : null;
      let startTimeToUse = rankedRolloverStart || chosenRanking?.suggestedStartTime || waiterFixed || safeFallback;
      
      // NEW: Enhanced validation for all ticket types
      let startTimeDate = new Date(startTimeToUse);
      
      // Past-time bump (H2 fix — paired with the toast below).
      //
      // Originally labelled "TUTORIAL FIX" but unconditional: any past
      // selection — tutorial or production — silently shifted to now+5min
      // with only a console.log. The Catalyst auto-assign path
      // (useCatalystAutoAssign.ts:178-184) has always toasted on the
      // equivalent clamp; OptiPlace now matches that UX so the advisor
      // knows the placement moved and can re-pick if needed.
      const now = clock.now();
      if (startTimeDate < now) {
        const originalIso = startTimeToUse;
        const adjustedTime = addMinutes(now, 5);
        startTimeDate = adjustedTime;
        startTimeToUse = adjustedTime.toISOString();

        console.log('[OptiPlace] Past-time bump:', {
          originalTime: originalIso,
          adjustedTime: adjustedTime.toISOString(),
          isTutorial: currentStepData !== null
        });
        toast({
          title: 'Start time moved to next available',
          description:
            'Selected time was in the past, so the ticket was moved to the next available time.',
        });
      }
      
      const finishTime = new Date(startTimeDate.getTime() + ticketData.predictedMinutes * 60000);
      
      // Validation 2: Finish time < due out (if specified)
      if (ticketData.dueOut) {
        const dueOut = new Date(ticketData.dueOut);
        if (finishTime > dueOut) {
          toast({
            variant: 'destructive',
            title: 'Cannot Accommodate',
            description: `Job would finish at ${format(finishTime, 'h:mm a')} but is promised for ${format(dueOut, 'h:mm a')}`,
          });
          setIsCreating(false);
          return;
        }
      }
      
      // Validation 3: Within business hours.
      //
      // Resolve the selected day's hours through the canonical
      // `getShopHoursForDay` helper (shopHoursUtils.ts) instead of an inline
      // key lookup. The previous code did
      // `shopHours[format(selectedDate, 'EEEE')]`, which (a) keyed on the
      // CAPITALIZED day name ('Tuesday') even though shop hours are canonically
      // stored lowercase ('tuesday' — onboarding's ShopBasicsStep writes that;
      // see ShopProfile.tsx:394), so the configured hours were never found and
      // validateTicketStartTime silently fell back to its 19:00 default close;
      // and (b) derived the weekday in the operator's browser-local time rather
      // than the shop timezone. The shared helper handles both day-name cases
      // and is timezone-correct. `?? undefined` keeps validateTicketStartTime's
      // "no hours → defaults" contract (it treats a falsy value as absent).
      const hoursForDay = shopHours
        ? getShopHoursForDay(selectedDate, shopHours, shopTimezone) ?? undefined
        : undefined;
      // DTC-E: pass shopTimezone so the validation compares startTimeDate
      // (a UTC instant) against the shop's wall-clock open/close, not the
      // operator's browser-local hours.
      const validation = validateTicketStartTime(startTimeDate, hoursForDay, shopTimezone);
      
      if (!validation.isValid) {
        toast({
          variant: 'destructive',
          title: 'Invalid Start Time',
          description: validation.error || 'The suggested start time is outside business hours. Please try a different time.',
        });
        setIsCreating(false);
        return;
      }
      
      // The rollover plan to persist: the server's parts, or — when the start
      // moved (past-time bump) — the same work re-planned from the actual
      // start over the workdays the server resolved (rolloverPlanClient.ts).
      const rankedRolloverParts = chosenRanking?.rolloverParts || [];
      let rolloverParts = rankedRolloverParts;
      if (rankedRolloverParts.length > 1) {
        const resolved = resolveRolloverPartsForStart({
          parts: rankedRolloverParts,
          plan: chosenRanking?.rolloverPlan ?? null,
          startIso: startTimeToUse,
          shopTimezone,
        });
        if (!resolved.ok) {
          toast({
            variant: 'destructive',
            title: 'Schedule changed',
            description: 'The multi-day plan no longer fits from the new start time. Refresh the recommendations and try again.',
          });
          setIsCreating(false);
          return;
        }
        rolloverParts = resolved.parts;
      }
      const willCreateRollover = rolloverParts.length > 1;
      if (willCreateRollover) startTimeToUse = rolloverParts[0].startTime;

      console.log('[OPTIPLACE] Rollover parts:', rolloverParts);

      // The persisted chain: Part A's schedule fields plus one lineage-linked
      // continuation per further part, book minutes conserving the ticket's
      // total (rolloverPlanClient.buildRolloverChain — shared with Catalyst).
      const rolloverChain = willCreateRollover
        ? buildRolloverChain({
            rootTicketId: ticketId,
            parts: rolloverParts,
            totalBookMinutes: ticketData.predictedMinutes,
            efficiencyPct: techEff,
            bufferPct: techBuffer,
            shopTimezone,
            nowMs: attempt.chainNowMs,
          })
        : null;

      // Create main ticket (Part A if rollover, otherwise full ticket)
      const mainPredictedMinutes = rolloverChain
        ? rolloverChain.root.predicted_minutes
        : ticketData.predictedMinutes;

      // Part A's shop-local date is its own planned workday — the selected day
      // unless nothing of it was left.
      const mainScheduledDate = rolloverChain
        ? rolloverChain.root.scheduled_date
        : selectedDayLabel;
      
      // ── Location-mode dispatch ──────────────────────────────────────────
      //
      // Two valid create shapes per the parking_lot_immunity contract
      // (migration 20260513120000) and the bay/parking-spot exclusion rule
      // (move_vehicle_location enforces these as mutually-exclusive at the
      // RPC level; the create path has to mirror that or the row never
      // becomes valid):
      //
      //   • PARKED-ON-ARRIVAL (operator picked a parking spot):
      //       parking_lot       = true
      //       parking_spot_id   = <chosen spot>
      //       bay_id            = null
      //       assigned_tech_id  = null         ← required by immunity
      //       scheduled_tech_id = <selectedTechId>   ← dispatch plan
      //
      //   • ASSIGNED-TO-BAY (no spot picked):
      //       parking_lot       = false
      //       parking_spot_id   = null
      //       bay_id            = null          ← B5a; see below
      //       assigned_tech_id  = <selectedTechId>
      //       scheduled_tech_id = (BEFORE trigger mirrors from assigned)
      //
      //     "ASSIGNED-TO-BAY" names the PLAN, not the car's whereabouts. A
      //     newly booked vehicle has not arrived, so it is not in a bay, and
      //     the row must not say it is. `bay_id` is earned by a physical move
      //     (`move_vehicle_location`), never by a booking.
      //
      // Pre-fix this branch wrote BOTH parking_lot=true AND assigned_tech_id
      // for parked-on-arrival, AND wrote a bay_id on top of the parking
      // spot — guaranteed to hit `tickets_parking_lot_immunity` and the
      // operator saw the toast "new row for relation 'tickets' violates
      // check constraint 'tickets_parking_lot_immunity'".
      const isParkedOnArrival = !!ticketData.parkingSpotId;

      // Skip the bay pick for parked-on-arrival — the vehicle isn't going
      // to a bay, so picking one is wasted work and stamps a bay_id we'd
      // immediately discard.
      let assignedBayId = isParkedOnArrival
        ? null
        : await pickCompatibleBayForTech({
            techId: selectedTechId,
            shopId,
            // jobs intentionally omitted — ticket_jobs snapshots are
            // written AFTER the ticket INSERT, so requirements aren't yet
            // available. Helper falls back to lowest-numbered bay.
          });

      // ── Bay-occupancy guard (uniq_active_bay protection) ──────────
      // pickCompatibleBayForTech is intentionally optimistic — for a
      // single-bay tech it always returns that bay even if it's
      // currently occupied by another Active/Paused ticket on the same
      // scheduled_date. That happens routinely when OptiPlace queues a
      // future drop-off behind the tech's current ticket (e.g. Joe
      // active at 8:30, OptiPlace dispatches the new ticket to Joe
      // starting at 10:39).
      //
      // If we hand bay_id through to the insert, the `uniq_active_bay`
      // partial unique index defined in migration
      // 20260512190000_uniq_active_bay_per_day.sql raises 23505.
      // Constraint semantics from that migration's docblock:
      //   "a bay is occupied when a vehicle is physically inside it"
      // — i.e. bay_id should be set when the vehicle has physically
      // moved into the bay, not at dispatch time.
      //
      // Therefore: when the chosen bay is currently held by another
      // Active/Paused ticket for the same scheduled_date, set
      // bay_id = null on the insert. The dispatch plan stays intact via
      // assigned_tech_id + scheduled_tech_id + start_time; the physical
      // bay gets assigned later through the canonical
      // `move_vehicle_location` RPC when the current ticket clears.
      if (assignedBayId) {
        const guard = await isBayCurrentlyOccupied({
          supabase,
          bayId: assignedBayId,
          scheduledDate: mainScheduledDate,
          excludeTicketId: ticketId,
        });
        if (!guard.ok) {
          console.warn(
            '[OPTIPLACE] unable to verify active bay occupancy before insert',
            guard.error,
          );
        } else if (guard.occupied) {
          console.log(
            '[OPTIPLACE] candidate bay currently occupied on the same scheduled_date; deferring physical bay assignment until current ticket clears',
            { bayId: assignedBayId, scheduledDate: mainScheduledDate, ticketId },
          );
          assignedBayId = null;
        }
      }

      // ── Parking-spot-occupancy guard (uniq_active_parking_spot protection) ──
      // Parked-on-arrival writes the OPERATOR-CHOSEN `parking_spot_id` directly.
      // The partial unique index `uniq_active_parking_spot` allows only one
      // active ticket per (parking_spot_id, scheduled_date), so a same-spot/day
      // collision raises 23505. Unlike the bay path we cannot silently defer to
      // "no spot" — the operator explicitly picked THIS spot — so check
      // occupancy first and fail with a clear, actionable message before the
      // insert. (`isSpotCurrentlyOccupied` mirrors the index's exact predicate,
      // is shop-scoped, and excludes this ticket's own id.)
      if (isParkedOnArrival && ticketData.parkingSpotId) {
        const spotGuard = await isSpotCurrentlyOccupied({
          supabase,
          parkingSpotId: ticketData.parkingSpotId,
          scheduledDate: mainScheduledDate,
          shopId,
          excludeTicketId: ticketId,
        });
        if (!spotGuard.ok) {
          console.warn(
            '[OPTIPLACE] unable to verify parking-spot occupancy before insert',
            spotGuard.error,
          );
          // Fall through: the DB unique index remains the backstop.
        } else if (spotGuard.occupied) {
          throw new Error(
            'This parking spot is already occupied for the selected day. Please choose a different spot.',
          );
        }
      }

      const assignedBay = assignedBayId ? { id: assignedBayId } : null;

      const ticketInsertPayload = {
        id: ticketId,
        creation_method: ticketData.creationMethod ?? 'create_ticket', // Core-100 R2 CORE-Y-GAP-001 / R4: origin recorded once
        // Core-100 R5 (CORE-AQ): created from a call proposal — the database confirms it with this ticket, atomically.
        ...(ticketData.sourceCallProposalId ? { source_call_proposal_id: ticketData.sourceCallProposalId } : {}),
        simple_ticket_number: 0, // Placeholder - trigger will set real value
        vehicle_id: vehicleData.id,
        customer_id: ticketData.customerId || null, // Link to customer profile
        customer_name: ticketData.customerName || null,
        customer_phone: ticketData.customerPhone || null,
        customer_request_notes: ticketData.customerComplaint || null,
        type: ticketData.type,
        priority: ticketData.priority,
        is_comeback: ticketData.isComeback,
        // Tech assignment depends on parked-on-arrival vs assigned-to-bay.
        // Both shapes carry the dispatch plan in scheduled_tech_id so the
        // Dayboard's widened tech-column filter (migration 20260601160000)
        // surfaces the ticket under the planned tech regardless of
        // whether assigned_tech_id is set right now.
        assigned_tech_id: isParkedOnArrival ? null : selectedTechId,
        // CORE-AB-GAP-008 / D3 — qualifies scheduled_tech_id, set
        // unconditionally below. A parked-on-arrival ticket still has a
        // dispatcher-chosen plan; only its custody is empty.
        dispatch_assignment_source: 'manual',
        scheduled_tech_id: selectedTechId,
        original_tech_id: selectedTechId, // PHASE 1: Set original tech on first assignment (immutable)
        // ── B5a: A DISPATCH PLAN IS NOT A PHYSICAL FACT ──────────────────
        // `tickets.bay_id` means "the vehicle is physically inside that bay"
        // — the semantics `uniq_active_bay` was written against, the reason
        // `tickets_lifecycle_terminal_state` forbids a terminal row from
        // holding one, and the reason the delete authority asks where the car
        // goes before releasing it.
        //
        // This used to be `assignedBay?.id || null`, i.e. whatever bay the
        // dispatch picker liked, nulled ONLY when that bay happened to be
        // occupied by someone else. An empty bay is not evidence that this
        // car is standing in it. Production carried 16 never-arrived tickets
        // marked as physically in a bay because of this line; one of them
        // could not be deleted, because the server correctly refused to
        // release a bay nobody could say the car had left.
        //
        // The plan is not lost — it lives in assigned_tech_id +
        // scheduled_tech_id + start_time and the per-job bay-type snapshots,
        // exactly as the location-mode comment above describes. The physical
        // bay is written later, by `move_vehicle_location`, which stamps
        // `bay_assignment_source = 'manual'` because a person asserted it.
        bay_id: null,
        start_time: startTimeToUse,
        scheduled_start_time: ticketData.type === 'Waiter' && ticketData.startTime
          ? convertDatetimeLocalToUTC(`${selectedDayLabel}T${ticketData.startTime}`, shopTimezone)
          : null,
        predicted_minutes: mainPredictedMinutes,
        due_out: ticketData.dueOut
          ? convertDatetimeLocalToUTC(ticketData.dueOut, shopTimezone)
          : null,
        odometer_miles: ticketData.odometerMiles || null,
        gvwr_lbs: ticketData.gvwrLbs || null,
        status: 'Active',
        shop_id: shopId,
        parent_ticket_id: null, // Parent ticket should NOT set itself as parent
        sequence_index: willCreateRollover ? 1 : null,
        remaining_minutes_at_split: rolloverChain
          ? rolloverChain.root.remaining_minutes_at_split
          : null,
        split_reason: willCreateRollover ? 'EOD_ROLLOVER' : null,
        scheduled_date: mainScheduledDate,
        rollover_completion_status: 'pending',
        required_predecessor_minutes: null, // First part has no predecessor
        created_by: currentUserId,
        parking_spot_id: ticketData.parkingSpotId || null,
        parking_lot: isParkedOnArrival,
      };

      // ══════════════════════════════════════════════════════════════════
      // ATOMIC REQUIRED-WRITE BLOCK
      // ══════════════════════════════════════════════════════════════════
      // Pre-atomic this issued three independent statements — tickets.insert,
      // jobs.insert, ticket_jobs.insert — plus a tickets.insert per rollover
      // part. On 2026-08-07 the third failed a CHECK constraint and the first
      // two stayed committed, leaving a live Active ticket with an empty Jobs
      // section (plus an orphaned jobs row) from a workflow that reported
      // failure. A client statement that already committed cannot be rolled
      // back by throwing in JavaScript.
      //
      // Everything a VALID ticket requires now goes through
      // `createTicketWithJobs` -> `create_ticket_with_jobs` (migration
      // 20260807130000), whose PL/pgSQL body is one transaction: any failure
      // rolls back every row, and no Realtime event fires for work that never
      // committed.
      //
      // So ALL payload construction happens first and NOTHING is written until
      // the single call below. The labor / tech-pay / bay-snapshot helpers were
      // already independent of the created job ids (they key off job.minutes and
      // job.category), so hoisting them above the write costs nothing.
      //
      // Optional side effects — rollover OCIS events, rollover_audit,
      // parts_lines, assignment_audit, logTicketCreated — deliberately stay
      // AFTER the commit. Their failure must not destroy a valid ticket.
      //
      // Relationship to OHF-A2: batched job creation. OHF-A2's goal was to stop
      // firing ~4 awaited round trips PER JOB, by building `jobsToInsert` and
      // `ticketJobsToInsert` as single index-aligned arrays over
      // `ticketData.jobs`. That payload construction is preserved verbatim here
      // — same columns, same coercions, same iteration order. What changes is
      // the delivery: OHF-A2's two batched `.insert()` calls (plus the separate
      // tickets insert) become ONE `createTicketWithJobs` call, which is
      // strictly fewer round trips than OHF-A2 achieved AND is transactional.
      // The per-job loop OHF-A2 removed must never come back.

      // Map bay requirement to valid bay_type (matching database enum values)
      const mapBayRequirement = (bay: string): string => {
        const bayLower = bay.toLowerCase();
        if (bayLower.includes('lift') || bayLower.includes('2-post') || bayLower.includes('4-post')) return 'Lift';
        if (bayLower.includes('flat')) return 'Flat';
        if (bayLower.includes('alignment')) return 'Alignment';
        if (bayLower.includes('specialty') || bayLower.includes('diagnostic')) return 'Specialty';
        return 'Lift'; // Default to Lift instead of 'Any' since 'Any' is not a valid enum value
      };

      // ── 1. Rollover Part B..N payloads (pure; no writes) ────────────────
      // `createdRolloverIds` stays index-aligned with `rolloverParts.slice(1)`
      // because it is filled in the same loop order the pre-atomic code used —
      // the downstream cascade anchoring at L2426/L2469 depends on that.
      // Ids are computed client-side (as before) so they are known before the
      // transaction rather than read back from it.
      const createdRolloverIds: string[] = [];
      const extraTicketPayloads: Record<string, unknown>[] = [];
      const rolloverPartMeta: Array<{
        index: number;
        rolloverTicketId: string;
        rolloverBookMinutes: number;
        rolloverDate: string;
        rolloverStartTime: Date;
      }> = [];

      if (willCreateRollover && rolloverParts.length > 1) {
        // 8G.2D.6A — a pre-created chain is still a CHAIN, not a fan.
        // Every part used to point `rollover_of_ticket_id` at the root, which
        // made Part C claim to follow Part A. Predecessor resolution now reads
        // that pointer as an identity, so the parts are linked in order —
        // by buildRolloverChain, the one place that shapes the lineage.
        for (let i = 1; i < rolloverParts.length; i++) {
          const link = rolloverChain!.continuations[i - 1];
          const rolloverTicketId = link.id;
          const rolloverStartTime = new Date(link.start_time);
          const rolloverBookMinutes = link.predicted_minutes;
          const rolloverDate = link.scheduled_date;

          extraTicketPayloads.push({
            id: rolloverTicketId,
            simple_ticket_number: 0, // Placeholder - trigger will set real value
            vehicle_id: vehicleData.id,
            customer_name: ticketData.customerName || null,
            customer_phone: ticketData.customerPhone || null,
            type: ticketData.type,
            priority: ticketData.priority,
            is_comeback: ticketData.isComeback,
            // Codex: vehicle_present derived from physical state (DB trigger)
            assigned_tech_id: selectedTechId,
            dispatch_assignment_source: 'manual', // CORE-AB-GAP-008 / D3
            original_tech_id: selectedTechId, // PHASE 1: Set original tech on rollover parts too
            // B5a: a continuation is NEXT DAY'S PLAN. It inherited
            // `assignedBay?.id` and so claimed a physical bay for work that
            // has not happened, on a day that has not arrived — while also
            // setting `parking_lot: true` below, a combination the server-side
            // create guard rejects outright as mutually exclusive.
            bay_id: null,
            start_time: link.start_time,
            predicted_minutes: rolloverBookMinutes,
            due_out: ticketData.dueOut
              ? convertDatetimeLocalToUTC(ticketData.dueOut, shopTimezone)
              : null,
            status: 'Active',
            shop_id: shopId,
            parent_ticket_id: link.parent_ticket_id,
            rollover_of_ticket_id: link.rollover_of_ticket_id,
            sequence_index: link.sequence_index,
            split_reason: link.split_reason,
            scheduled_date: rolloverDate,
            rollover_completion_status: 'pending',
            required_predecessor_minutes: mainPredictedMinutes,
            accumulated_bump_minutes: 0,
            created_by: currentUserId,
            // Unified rollover fields
            rollover_type: 'created',
            parking_lot: true,
            parking_spot_id: null,
          });

          createdRolloverIds.push(rolloverTicketId);
          rolloverPartMeta.push({
            index: i,
            rolloverTicketId,
            rolloverBookMinutes,
            rolloverDate,
            rolloverStartTime,
          });
        }
      }

      // ── 2. jobs payloads ───────────────────────────────────────────────
      const mapBayRequirementForBatch = (bay: string): string => mapBayRequirement(bay);
      const jobsToInsert = ticketData.jobs.map((job) => ({
        category: job.category,
        submenu: job.submenu,
        name: job.name,
        catalog_job_id: job.catalog_job_id ?? null,
        standard_minutes: job.minutes,
        bay_requirement: mapBayRequirementForBatch(job.bay),
        skill_level: job.skill,
        proficiency_category: job.proficiency_category || null,
        shop_id: shopId,
      }));

      // ── 3. labor / tech-pay columns (parallel; no job ids needed) ───────
      // techPay reads labor.sold_minutes so it stays sequential WITHIN each
      // job, but the jobs don't depend on each other — Promise.all collapses
      // J × 2 awaited helper calls into a single async wave.
      const ticketJobHelperCols = await Promise.all(
        ticketData.jobs.map(async (job) => {
          const labor = await buildLaborColumnsForAutoWrite({
            shopId,
            effectiveMinutes: job.minutes,
            jobCategory: job.category ?? null,
            flags: { labor_engine_enabled: laborEngineEnabled, ui_ticket_financials: ticketFinancialsEnabled },
          });
          const techPay = await buildTechPayColumnsForAutoWrite({
            shopId,
            technicianId: selectedTechId ?? null,
            referenceMinutes: job.minutes,
            retailSoldMinutes: (labor as any)?.sold_minutes ?? null,
            flags: { labor_engine_enabled: laborEngineEnabled, ui_technician_pay: techPayEnabled },
          });
          return { labor, techPay };
        }),
      );

      // ── 4. bay-rule snapshots ──────────────────────────────────────────
      // Without these the downstream bay-gating layers
      // (computeAllowedBaysForTicket / globalCascadeBayGating) read empty
      // arrays and silently treat every ticket as "any bay is fine" — which is
      // how a suspension / axle job (lift-required) ends up in a Flat bay.
      const baySnapshots = await buildBaySnapshotsForJobs(
        shopId,
        ticketData.jobs.map(j => ({
          catalog_job_id: (j as any).catalog_job_id ?? null,
          category: j.category ?? null,
        })),
      );

      // ── 5. ticket_jobs payloads ────────────────────────────────────────
      // `ticket_id` / `job_id` are deliberately ABSENT: the RPC injects both
      // inside the transaction, so the link can never reference an id that
      // failed to commit. Supplying either is rejected server-side.
      const ticketJobsToInsert = ticketData.jobs.map((job, idx) => ({
        minutes_override: job.minutes,
        ...(ticketJobHelperCols[idx].labor ?? {}),
        ...(ticketJobHelperCols[idx].techPay ?? {}),
        ...baySnapshots[idx],
      }));

      // ── 6. THE single atomic write ─────────────────────────────────────
      console.log('[OPTIPLACE] creating ticket atomically', {
        ticketId,
        location_mode: isParkedOnArrival ? 'parked_on_arrival' : 'assigned_to_bay',
        // The picker's answer is the PLANNED lane, recorded for diagnostics
        // only. It is deliberately NOT written to bay_id (B5a).
        planned_bay_candidate: assignedBay?.id ?? null,
        assigned_tech_id: ticketInsertPayload.assigned_tech_id,
        scheduled_tech_id: ticketInsertPayload.scheduled_tech_id,
        bay_id: ticketInsertPayload.bay_id,
        parking_spot_id: ticketInsertPayload.parking_spot_id,
        parking_lot: ticketInsertPayload.parking_lot,
        jobCount: jobsToInsert.length,
        rolloverPartCount: extraTicketPayloads.length,
      });

      // ONE bundle: the primary ticket group, then one group per rollover
      // continuation. Continuations carry no jobs of their own -- their work
      // lives on the parent -- which the RPC permits only because they carry
      // `rollover_of_ticket_id`.
      let bundleResult: Array<{ ticketId: string; jobIds: string[] }>;
      if (attempt.submitted) {
        // A previous attempt was sent. If it committed, the ticket is there:
        // finish as created, never create it again.
        const { data: alreadyCreated } = await supabase
          .from('tickets').select('id').eq('id', ticketId).maybeSingle();
        if (alreadyCreated) {
          if (ticketData.createOperationId) clearCreateOperation(ticketData.createOperationId);
          toast({
            title: 'Ticket already created',
            description: 'The earlier attempt reached the server, so nothing was created twice.',
          });
          void notifyDayboardInvalidated({ reason: 'ticket-create', shopId, ticketId, scheduledDate: format(selectedDate, 'yyyy-MM-dd'), metadata: { techId: selectedTechId } });
          onAssignmentComplete(ticketId);
          onClose();
          return;
        }
      }
      attempt.submitted = true;
      try {
        // B-W5 (LEDGER D-3): the client attests what its rollover preview showed; the server records it as
        // client-attested (never as server-verified) next to what the transaction actually committed.
        const rolloverPreview = extraTicketPayloads.length > 0
          ? buildOptiPlaceRolloverAttestation({
              shownParts: rankedRolloverParts,
              committedParts: rolloverParts,
              badgeShown: !!(chosenRanking?.isRollover || chosenRanking?.willRollover),
            })
          : undefined;
        bundleResult = await createTicketBundle([
          {
            ticket: ticketInsertPayload,
            jobs: jobsToInsert,
            ticketJobs: ticketJobsToInsert,
          },
          ...extraTicketPayloads.map((t, i) => ({
            ticket: t, jobs: [], ticketJobs: [],
            ...(i === 0 && rolloverPreview ? { rolloverPreview } : {}),
          })),
        ]);
      } catch (createErr) {
        // Defense-in-depth: if the pre-check above lost a TOCTOU race, the
        // `uniq_active_parking_spot` index still raises 23505. Convert that raw
        // unique violation into the same clear, actionable message instead of
        // surfacing a postgres constraint string to the operator.
        const tErr = createErr as { code?: string; message?: string; details?: string };
        // Core-100 R2 CORE-Z: the database also refuses a spot another car PHYSICALLY holds
        // (LOCATION_OCCUPIED, backed by vlc_spot_exclusive) — the whole creation rolled back.
        const isSpotConflict =
          tErr.code === '23505' &&
          /uniq_active_parking_spot|vlc_spot_exclusive|LOCATION_OCCUPIED/.test(`${tErr.message ?? ''} ${tErr.details ?? ''}`);
        if (isSpotConflict) {
          throw new Error(
            'This parking spot is already occupied. Please choose a different spot.',
          );
        }
        // The same operation committed concurrently (the original request was
        // still running when this retry was sent): its primary key refused a
        // second copy. Resolve to the original rather than reporting an error.
        if (tErr.code === '23505') {
          const { data: original } = await supabase.from('tickets').select('id').eq('id', ticketId).maybeSingle();
          if (original) {
            if (ticketData.createOperationId) clearCreateOperation(ticketData.createOperationId);
            toast({ title: 'Ticket already created', description: 'The earlier attempt reached the server, so nothing was created twice.' });
            void notifyDayboardInvalidated({ reason: 'ticket-create', shopId, ticketId, scheduledDate: format(selectedDate, 'yyyy-MM-dd'), metadata: { techId: selectedTechId } });
            onAssignmentComplete(ticketId);
            onClose();
            return;
          }
        }
        throw createErr;
      }

      // Past this line the ticket, its rollover parts, its jobs and their
      // ticket_jobs links are all committed. Nothing below may assume it can
      // undo them, and nothing above may have announced them.

      // Phase 4B `tempJobIdToRealJobId`: the RPC returns job ids in input
      // order, which replaces the previous reliance on the batched
      // `.insert(array).select()` response ordering. `createTicketWithJobs`
      // already rejects a count mismatch, so index alignment is guaranteed
      // before we get here.
      // Group 0 is the primary ticket; its jobIds are index-aligned with
      // ticketData.jobs, and createTicketBundle already rejected a count mismatch.
      const atomicResult = bundleResult[0];
      const tempJobIdToRealJobId = new Map<string, string>();
      ticketData.jobs.forEach((job, idx) => {
        if ((job as any).tempJobId) {
          tempJobIdToRealJobId.set((job as any).tempJobId, atomicResult.jobIds[idx]);
        }
      });

      // ── 7. Rollover OPTIONAL side effects (post-commit) ────────────────
      // Pre-atomic these ran inside the per-part insert loop, interleaved with
      // the required write, and a failure there aborted creation and triggered
      // a compensating delete of earlier parts. The parts are now committed
      // together, so these are pure after-the-fact logging: each is
      // individually non-fatal.
      for (const meta of rolloverPartMeta) {
        const partLetter = String.fromCharCode(65 + meta.index);
        console.log(`[EOD-ROLLOVER] Created Part ${partLetter} (${meta.rolloverTicketId}): ${meta.rolloverBookMinutes} book min at ${format(meta.rolloverStartTime, 'h:mm a')} on day +${meta.index}`);

        // OCIS-R71-FIX (HIGH-2): rollover part tickets previously had
        // no ticket_created event. Emit one per part with linkage back
        // to the primary ticket for cross-day timeline navigation.
        logTicketEventToOcis({
          ticketId: meta.rolloverTicketId,
          shopId,
          eventType: 'ticket_created',
          content: `Ticket created via OptiPlace EOD rollover (Part ${partLetter})`,
          authorUserId: currentUserId || undefined,
          payload: {
            ticketType: ticketData.type,
            totalMinutes: Math.max(1, meta.rolloverBookMinutes),
            via: 'optiplace_eod_rollover',
            rollover_of_ticket_id: ticketId,
            parent_ticket_id: ticketId,
            sequence_index: meta.index + 1,
            scheduled_date: meta.rolloverDate,
          },
        }).catch(err => console.error('[OptiPlace] Rollover OCIS log error:', err));

        // Log to rollover audit table. Optional: a failed audit row must not
        // invalidate the committed rollover parts, so it is logged not thrown.
        const { error: rolloverAuditErr } = await supabase.from('rollover_audit').insert({
          ticket_id: ticketId,
          part_a_id: ticketId,
          part_b_id: meta.rolloverTicketId,
          split_date: format(selectedDate, 'yyyy-MM-dd'),
          today_minutes: rolloverChain!.root.predicted_minutes,
          remaining_minutes: meta.rolloverBookMinutes,
          original_tech_id: selectedTechId,
          // DTC-E: `startTimeToUse` is a UTC ISO string (suggestedStartTime,
          // waiterFixed, or selectedDate.toISOString() — all UTC). Reading
          // raw `.getHours()` would return BROWSER-local hours and silently
          // corrupt the split boundary for cross-TZ operators. Route through
          // canonical shop-tz minute helper.
          // The split boundary is Part A's planned finish (break-aware), not
          // start + work minutes.
          split_minute: getMinutesInShopTZ(rolloverParts[0].finishTime, shopTimezone),
          eod_allowance: 0,
        });
        if (rolloverAuditErr) {
          console.error('[EOD-ROLLOVER] rollover_audit insert failed (parts already committed):', rolloverAuditErr);
        }
      }

      // NOTE: We do NOT duplicate jobs to rollover tickets.
      // Rollover tickets represent remaining TIME to work on the same vehicle/jobs.
      // The job records stay on the parent ticket only.

      // Phase 4B: persist staged parts (if any). Job-linked parts use the
      // explicit map; ticket-level parts pass job_id = null.
      if (ticketData.stagedParts && ticketData.stagedParts.length > 0) {
        const partsRows = ticketData.stagedParts.map((p) => {
          const resolvedJobId = p.tempJobId ? tempJobIdToRealJobId.get(p.tempJobId) ?? null : null;
          if (p.tempJobId && !resolvedJobId) {
            console.warn('[Phase4B] staged part references unknown tempJobId — falling back to ticket-level', p.tempJobId);
          }
          return {
            shop_id: shopId,
            ticket_id: ticketId,
            job_id: resolvedJobId,
            vendor_name: p.vendor_name,
            vendor_part_number: p.vendor_part_number,
            description: p.description,
            category: p.category,
            quantity: p.quantity,
            unit_cost: p.unit_cost,
            actual_sell_each: p.unit_sell,
            actual_sell_total: null,
            pricing_source: 'manual' as const,
            pricing_method: 'manual_entry' as const,
            manual_price_locked: p.unit_sell != null,
            taxable: p.taxable,
            status: 'needed' as const,
            core_charge: p.core_charge,
            source_type: 'manual',
            source_reference: null,
            notes: p.notes,
            created_by: currentUserId ?? null,
          };
        });
        const { error: partsErr } = await (supabase as any)
          .from('parts_lines')
          .insert(partsRows);
        if (partsErr) {
          console.error('[Phase4B] failed to insert staged parts (ticket already created):', partsErr);
          toast({
            title: 'Ticket created, but some parts failed to save',
            description: partsErr.message ?? 'You can re-add them from the Parts & Labor tab.',
            variant: 'destructive',
          });
        }
      }

      breadcrumbs.log('OB:COMMIT', { action: 'ticket_created', ticketId, techId: selectedTechId });

      // Log ticket creation and assignment to pensieve for audit trail
      const userId = currentUserId || 'system';
      const techName = rankings.find(r => r.technicianId === selectedTechId)?.technicianName;
      
      await logTicketCreated({
        ticketId,
        shopId,
        userId,
        ticketType: ticketData.type as 'Waiter' | 'Drop-off',
        totalMinutes: mainPredictedMinutes,
        promisedTime: ticketData.dueOut || undefined,
        scheduledStartTime: startTimeToUse,
      });
      
      await logTicketAssigned({
        ticketId,
        shopId,
        userId,
        techId: selectedTechId,
        techName: techName || undefined,
        startTime: startTimeToUse,
      });

      // Ranking audit (the Ranking tab). Core-100 R5 (CORE-AB S): the rows are the SERVER's — smart-assign recorded
      // this analysis as a ranking run; the browser only attaches that run to the ticket it just created and names
      // the technician chosen. It never writes a score. Not awaited (ticket creation does not wait on the audit),
      // but a failure is reported, not dropped.
      void recordAssignmentSelection({
        rankingRecord: rankingRecordRef.current,
        ticketId,
        selectedTechId: selectedTechId ?? null,
        shopId,
        source: 'optiplace',
      });

      // OPS-A5 / OPS-A1 / OPS-A4: narrowed post-create invariant scan.
      // Pre-OPS-A5 this block fetched the ENTIRE shop's tickets with
      // `select('*, technician:technicians!assigned_tech_id(*)')` + ALL
      // technicians with `*` + shop_profile with `*` after every assignment
      // — O(historical tickets) payload bloat with no date filter, no
      // LIMIT, breadcrumb-only consumer (no UI block, no rollback).
      //
      // OPS-A4 audit settled the per-rule policy:
      //   - Rule 1 NO_OVERLAPS / Rule 1B BAY_GLOBAL_OVERLAP / Rule 4
      //     PARKING_LOT_IMMUNITY are DB-enforced (CSI-I1a CHECK +
      //     CSI-I1b EXCLUDE constraints, migrations 20260512+/20260513+).
      //     JS layer was redundant.
      //   - Rule 5 BAY_TYPE_REQUIREMENTS is CSI-I3-A4 SAFE — `requires_lift`
      //     has zero writers; canonical lift safety is GVWR vs
      //     lift_capacity_lbs in move_vehicle_location RPC.
      //   - Rule 6 BAY_ASSIGNMENT_SYNC is no-op here (bays=[]).
      //   - Rule 3 WAITER_EOD (CSI-J1, NOT DB-enforced) is the SOLE
      //     non-redundant signal at this call site. CSI-J1's docblock
      //     explicitly cites THIS OptiPlaceModal integration as Rule 3's
      //     live observability surface; OPS-A5 preserves it.
      //
      // Narrowed shape:
      //   - tickets scoped to {primary scheduled_date, ...rollover dates}
      //     × status IN ('Active','Paused'), narrow ~10-column projection
      //     (no `*`, no nested join — the previous `technician(*)` join
      //     was OVERFETCH; no rule reads `ticket.technician`).
      //   - shopTimezone reused from TimezoneContext (no shop_profile
      //     re-fetch — only `time_zone` was ever read at this site).
      //   - technicians scoped to the assigned_tech_id set actually
      //     present in the scoped tickets, narrow projection (id, name,
      //     hours, shift_end, time_off — exactly the fields Rule 3 reads).
      // CORE-BQ Unit 7 — THE BAY-OCCUPANCY POPULATION, NOT A DATE SLICE.
      //
      // This scan used to select {selected day, ...rollover days} × Active or
      // Paused. Both narrowings omitted rows the bay-overlap rules govern:
      //
      //   STATUS. `tickets_no_global_bay_overlap` applies to every row holding
      //   a bay_id that is not Completed. Blocked is such a row, and the old
      //   filter never loaded it — so an Active ticket double-booked against a
      //   Blocked one read as a clean bay.
      //
      //   DATE. That constraint names no date column at all; it keys on
      //   tstzrange(start_time, opti_finish). A run-long ticket dated to
      //   yesterday can occupy a bay today, and `scheduled_date` cannot see it.
      //
      // Read live from the replayed database, `tickets_lifecycle_terminal_state`
      // forbids a Completed / Cancelled / Deleted row from holding a bay_id at
      // all. So Active + Paused + Blocked is not a wider guess — it is exactly
      // the set of statuses that can physically occupy a bay, which makes this
      // query the COMPLETE population for both overlap rules.
      //
      // It is also naturally bounded: it is scoped by open operational state
      // rather than by time, so a shop's terminal history cannot grow it.
      // Measured on a disposable local fixture of 6,000 terminal rows: none
      // matched, and the scan returned 177 rows in 1.3 ms.
      //
      // No interval window was introduced. A window around the placement would
      // reintroduce the same class of hole from the other direction — a ticket
      // outside the window can still overlap one inside it, and the auditor
      // reports every conflicting PAIR, not merely conflicts with the ticket
      // being placed.
      //
      // The projection is unchanged. Widening it is a different unit.
      const { data: scanTickets, error: scanTicketsError } = await supabase
        .from('tickets')
        .select('id, assigned_tech_id, bay_id, start_time, opti_finish, status, scheduled_date, parking_lot, type, deleted_at')
        .eq('shop_id', shopId)
        .in('status', ['Active', 'Paused', 'Blocked']);

      if (scanTicketsError) {
        // CORE-BQ responsibility 7: an auditor that cannot run must say so.
        // This previously destructured only `data`, so a failed query left
        // `scanTickets` null and the whole audit was skipped in silence —
        // indistinguishable from a board with nothing to check.
        breadcrumbs.log('OB:GUARD', {
          action: 'invariant_audit_incomplete',
          ticketId,
          reason: 'scan_query_failed',
        });
      }

      const scanTechIds = Array.from(new Set(
        ((scanTickets || []) as Array<{ assigned_tech_id: string | null }>)
          .map((t) => t.assigned_tech_id)
          .filter((id): id is string => !!id)
      ));
      let scanTechs: any[] = [];
      if (scanTechIds.length > 0) {
        const { data, error: scanTechsError } = await supabase
          .from('technicians')
          .select('id, name, hours, shift_end, time_off')
          .in('id', scanTechIds);
        if (scanTechsError) {
          // CORE-BQ Unit 9, same fail-safe Unit 7 gave the ticket scan. This
          // destructured only `data`, so a failed roster query yielded `[]` and
          // WAITER_EOD skipped every ticket in silence — its per-ticket guard
          // is `if (!tech) return`, so an empty roster reads as a clean board
          // rather than as an audit that never ran.
          breadcrumbs.log('OB:GUARD', {
            action: 'invariant_audit_incomplete',
            ticketId,
            reason: 'technician_scan_query_failed',
          });
        }
        scanTechs = data || [];
      }

      if (scanTickets) {
        breadcrumbs.log('OB:GUARD', { action: 'optiplace_invariant_check', ticketId });
        // CORE-BQ Unit 1. `null` for bays, not `[]`.
        //
        // This scan loads tickets and technicians and does NOT load bays or the
        // specialty planes. It used to pass a literal `[]` for bays, which the
        // checker could only read as "this shop has no bays" — so
        // BAY_TYPE_REQUIREMENT and BAY_SYNC examined nothing, nine specialty
        // rules never ran, and the result still said `valid: true`. Passing
        // `null` says what is true: that dataset was never fetched.
        //
        // Widening the scan is deliberately NOT done here. This unit makes the
        // audit honest about its coverage; extending the coverage is a separate
        // decision with its own query cost.
        const result = checkScheduleInvariants(
          scanTickets,
          scanTechs,
          null,
          { time_zone: shopTimezone },
          undefined,
          undefined,
          // CORE-BQ Units 6 and 7. What the query above ACTUALLY loaded.
          //
          // The DATASET stays FILTERED. The scan still selects only three of
          // the six statuses, and what that population means has been proven
          // for four rules — not for every rule that reads tickets. Unit 7
          // proved the two bay-overlap rules and Unit 9 the two advisories;
          // PARKING_LOT_IMMUNITY, BAY_TYPE_REQUIREMENT and the specialty rules
          // still read tickets with no established domain, and flipping the
          // dataset would hand the stronger claim to all of them at once.
          //
          // `scheduled_date_subset` is gone because that filter is gone.
          //
          // Technicians are still fetched only for the ids the tickets
          // referenced: sufficient for the tickets supplied, and still not the
          // shop's roster.
          //
          // `shop_id` is NOT a defect and is not listed: a shop-scoped audit is
          // the intended universe, not an omission from it.
          {
            tickets: { status: 'FILTERED', reasons: ['status_subset'] },
            technicians: { status: 'FILTERED', reasons: ['referenced_ids_only'] },
            // bays and the specialty planes are not passed at all, so they keep
            // their missing-input reading rather than gaining a scope claim.

            // The two rules whose population this query genuinely completes.
            //
            // Both are reachable only by rows holding a bay_id, and the live
            // `tickets_lifecycle_terminal_state` forbids Completed / Cancelled
            // / Deleted rows from holding one — proved by executing the live
            // constraints, not by reading them. So every row that could change
            // either rule's answer is in this result set, and no date filter
            // truncates it.
            //
            // Named one rule at a time on purpose: a stronger claim should have
            // to be argued for the rule it applies to.
            // CORE-BQ Unit 9 adds the other two, and the reason is different.
            //
            // The first pair qualified because a bay_id is unreachable from a
            // terminal status, so their rows were already all here. These two
            // read no bay at all; they qualified only once their DOMAIN was
            // established, which is what Unit 9 did. Both now decline to speak
            // about a row that is terminal by status or carries a `deleted_at`
            // tombstone — so their domain is exactly the non-terminal statuses,
            // and `IN ('Active','Paused','Blocked')` is that set, named
            // exhaustively rather than sampled. A tombstoned row with an
            // operational status is loaded and then skipped by the rule, which
            // is a superset, and a superset is what EXHAUSTIVE requires.
            //
            // The population claim is therefore EARNED BY the domain repair,
            // not asserted alongside it: before Unit 9 these rules ranged over
            // rows this query does not load, and the claim would have been
            // false.
            //
            // WAITER_EOD gets the ticket axis only. Its technicians stay
            // FILTERED, because its `if (!tech) return` cannot distinguish
            // "this technician has no shift problem" from "this technician was
            // not supplied", and a rule that can silently skip a row has not
            // earned a completeness claim on the plane it skipped on.
            ruleOverrides: {
              NO_OVERLAPS: { tickets: { status: 'EXHAUSTIVE' } },
              BAY_GLOBAL_OVERLAP: { tickets: { status: 'EXHAUSTIVE' } },
              NO_PAST_TIMES: { tickets: { status: 'EXHAUSTIVE' } },
              WAITER_EOD: { tickets: { status: 'EXHAUSTIVE' } },
            },
          },
        );
        const coverage = summariseInvariantCoverage(result.coverage);

        // CORE-BQ Slice B (Definition of Done item 1). Until now every audit
        // this modal ran was computed, shown once and discarded, so a shop
        // could not know whether the same contradiction had happened fifty
        // times last month or once — and a recurring violation is evidence
        // about a WRITER, which is the signal that discarding them destroyed.
        //
        // The verdict is not sent. The database derives it from the findings
        // and the coverage, so one rule has one implementation.
        //
        // Recorded for every outcome, not only failures: a completed audit that
        // found nothing is the only thing that can ever license the word
        // "healthy", so it is precisely the case that must be persisted.
        //
        // The outcome is CHECKED rather than discarded. An ignored write is
        // telemetry, not a control — the failure shape migration
        // 20260919120000 recorded from a proven case. A failure leaves no run,
        // which the integrity surface reports as "not audited"; it can never
        // read as a clean board.
        const recorded = await recordIntegrityAudit(supabase as never, {
          shopId,
          entryPoint: 'schedule',
          source: 'optiplace',
          result,
        });
        if (!recorded.ok) {
          breadcrumbs.log('OB:GUARD', {
            action: 'integrity_audit_not_persisted',
            ticketId,
            reason: recorded.reason,
          });
        }

        if (result.verdict === 'FAIL') {
          // Unchanged behaviour, plus the coverage that produced it — a
          // violation list is easier to act on when you also know what was not
          // looked at.
          breadcrumbs.log('OB:FAIL', {
            action: 'invariant_violations',
            violations: result.violations,
            coverage,
          });
          console.error('Schedule invariant violations after OptiPlace:', result.violations);
        } else if (result.verdict === 'INCOMPLETE') {
          // NOT an acknowledgement. Nothing contradictory was found, and the
          // audit did not finish, and those are different statements. Emitting
          // the PASS marker here is the exact overclaim CORE-BQ responsibility 8
          // forbids.
          breadcrumbs.log('OB:GUARD', {
            action: 'invariant_audit_incomplete',
            ticketId,
            coverage,
          });
        } else {
          // PASS: nothing found AND everything intended was evaluated. The
          // checked rules travel with the acknowledgement so the claim is
          // bounded by what it actually covered.
          breadcrumbs.log('OB:ACK', {
            action: 'invariant_audit_passed',
            ticketId,
            coverage,
          });
        }
      }

      breadcrumbs.log('OB:ACK', { action: 'ticket_assignment_complete', ticketId });

      // Show success toast with rollover info if applicable
      if (willCreateRollover && createdRolloverIds.length > 0) {
        const partLabels = rolloverParts.map((part, idx) => 
          `Part ${String.fromCharCode(65 + idx)}: ${part.minutes}min`
        ).join(', ');
        toast({
          title: `Ticket Created - Splits across ${rolloverParts.length} days`,
          description: `Ticket ${ticketId} assigned to ${techName}. ${partLabels}`,
          duration: 6000,
        });
      } else {
        toast({
          title: 'Ticket Created Successfully',
          description: `Ticket ${ticketId} assigned to ${techName}`,
        });
      }

      // H1 fix — anchor the newly placed ticket(s) so the post-write
      // cascade walker treats them as locked and walks the cursor to
      // their opti_finish without re-placing them. Without this, a
      // flexible Drop-off ticket whose user-chosen start_time sits
      // LATER than `cursor + 1` (e.g., shift start) was silently
      // pulled back by the cascade — the user's selected time was
      // ignored. The anchor mechanism on triggerBoardCascade
      // (cascadeTrigger.ts:84-89) is the same one useAutoBump uses
      // to protect its not-started branch placement. Waiter behavior
      // is unaffected (they were already protected by their
      // scheduled_start_time lock at _shared/ticketLocks.ts:42).
      const { triggerBoardCascade } = await import('@/utils/cascadeTrigger');
      const primaryDate = willCreateRollover ? mainScheduledDate : format(selectedDate, 'yyyy-MM-dd');
      await triggerBoardCascade(shopId, primaryDate, selectedTechId, [ticketId]);

      // MED-2: Cascade each unique rollover date beyond the primary.
      // Each rollover part (B, C, ...) gets its own ticket id in
      // `createdRolloverIds` with index aligned to `rolloverParts.slice(1)`
      // (see L1830-L1895). Anchor each cascade with the matching id so
      // the rollover continuation's user-derived start time is also
      // protected from the walker's overwrite.
      if (willCreateRollover && rolloverParts && rolloverParts.length > 1) {
        const rolloverTail = rolloverParts.slice(1);
        for (let idx = 0; idx < rolloverTail.length; idx++) {
          const part = rolloverTail[idx];
          const rd = getShopDayLabel(new Date(part.startTime).toISOString(), shopTimezone);
          if (rd && rd !== primaryDate) {
            const anchorId = createdRolloverIds[idx];
            await triggerBoardCascade(
              shopId,
              rd,
              selectedTechId,
              anchorId ? [anchorId] : undefined,
            );
          }
        }
      }

      // Cross-device Dayboard sync
      void notifyDayboardInvalidated({
        reason: 'ticket-create',
        shopId,
        ticketId,
        scheduledDate: format(selectedDate, 'yyyy-MM-dd'),
        metadata: { techId: selectedTechId },
      });

      if (ticketData.createOperationId) clearCreateOperation(ticketData.createOperationId);
      onAssignmentComplete(ticketId);
      onClose();
    } catch (error: any) {
      breadcrumbs.log('OB:FAIL', { action: 'optiplace_error', error: error.message });
      // Postgrest errors carry { message, code, details, hint }. A
      // constraint violation surfaces with code (SQLSTATE) and details
      // ("Key (X)=(Y) ...") that name the failing constraint. Without
      // these in the toast the operator sees only "violates check
      // constraint" — actionable for engineers but not the user.
      const pgCode    = error?.code    || null;
      const pgDetails = error?.details || null;
      const pgHint    = error?.hint    || null;
      const description = [
        error?.message || 'Unknown error',
        pgCode    ? `code=${pgCode}`       : null,
        pgDetails ? `details=${pgDetails}` : null,
        pgHint    ? `hint=${pgHint}`       : null,
      ].filter(Boolean).join(' · ');
      console.error('[OPTIPLACE] create ticket failed', {
        message: error?.message,
        code: pgCode,
        details: pgDetails,
        hint: pgHint,
        error,
      });
      // Core-100 R5 (CORE-AQ): a refused call proposal is said in words the advisor can act on — nothing was created.
      const callRefusal = callProposalRefusalMessage(error);
      if (callRefusal) {
        toast({ title: 'Call suggestion refused', description: callRefusal, variant: 'destructive' });
        onCallProposalRefused?.(callRefusal);
        onClose();
        return;
      }
      // OCT28 #12: the placement guard refused a window that was valid when it was ranked (PT409). Nothing was created;
      // say why in plain words and re-rank so the dispatcher chooses from current capacity — never override the guard.
      const placementRefusal = placementRefusalReason(error);
      if (placementRefusal) {
        toast({ ...placementRefusalMessage(placementRefusal), variant: 'destructive' });
        setSelectedTechId(null);
        void analyzeAssignmentsForDate(selectedDate);
        return;
      }
      toast({
        title: 'Error Creating Ticket',
        description,
        variant: 'destructive',
      });
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent data-testid="optiplace-modal" className="max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Zap className="h-5 w-5 text-primary" />
              OptiPlace - OptiAssign
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsCalendarOpen(true)}
                className="flex items-center gap-2"
              >
                <Calendar className="h-4 w-4" />
                {format(selectedDate, 'MMM d, yyyy')}
              </Button>
            </div>
          </DialogTitle>
          <DialogDescription>
            AI-powered technician assignment based on skills, availability, and workload
          </DialogDescription>
        </DialogHeader>
        {/* Core-100 R2 CORE-BR-GAP-001: the shop's outdoor-weather policy, beside the scheduling decision (a notice, never a filter). */}
        <SchedulingWeatherAdvisory shopId={activeShop?.id} />

        <div className="space-y-4">
          {/* Ticket Summary */}
          <Card>
            <CardContent className="pt-6">
              <div className="space-y-3 text-sm">
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4 text-muted-foreground" />
                  <span className="font-medium">Vehicle:</span>
                  <span>{ticketData.vehicle ? `${ticketData.vehicle.year} ${ticketData.vehicle.make} ${ticketData.vehicle.model}` : 'Existing vehicle'}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Wrench className="h-4 w-4 text-muted-foreground" />
                  <span className="font-medium">Jobs:</span>
                  <span>
                    {ticketData.jobs.map((job, idx) => (
                      <span key={idx}>
                        {job.name} ({job.minutes} min){idx < ticketData.jobs.length - 1 ? ', ' : ''}
                      </span>
                    ))}
                  </span>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <span className="font-medium">Type:</span>
                  <Badge variant="outline">{ticketData.type}</Badge>
                  <Badge variant={ticketData.priority === 'Emergency' ? 'destructive' : 'secondary'}>
                    {ticketData.priority}
                  </Badge>
                  {/* Manual Bay Indicator - show when reassigning a ticket with manual bay assignment */}
                  {ticketData.id && isManualBay && (
                    <Badge variant="outline" className="border-amber-500/50 bg-amber-500/10 text-amber-700 dark:text-amber-400">
                      🔒 Manual Bay Set
                    </Badge>
                  )}
                </div>
                {ticketData.isComeback && lastTechId && (
                  <p className="text-xs text-muted-foreground pl-6">
                    🚨 This vehicle was last serviced by {rankings.find(r => r.technicianId === lastTechId)?.technicianName || 'a technician'}
                  </p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Loading State */}
          {isAnalyzing && rankings.length === 0 && (
            <div className="flex flex-col items-center justify-center py-8 space-y-4">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-muted-foreground">Analyzing technicians...</p>
            </div>
          )}

          {/* No Availability Message */}
          {noAvailabilityMessage && !isAnalyzing && rankings.length === 0 && (
            <Card className="border-destructive bg-destructive/10">
              <CardContent className="pt-6">
                <div className="flex flex-col items-center justify-center space-y-3 text-center">
                  <AlertCircle className="h-8 w-8 text-destructive" />
                  <p className="font-medium text-destructive">{noAvailabilityMessage}</p>
                  <p className="text-sm text-muted-foreground">
                    Try selecting a different date manually or check technician schedules.
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          {splitHeldBackBy.length > 0 && !isAnalyzing && (
            <CultureInfluenceNote items={splitHeldBackBy} testId="optiplace-split-held-back" />
          )}

          {/* Split Suggestion Card */}
          {splitSuggestion?.eligible && !isAnalyzing && (
            <Card className="border-amber-500 bg-amber-50 dark:bg-amber-950/20">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <AlertCircle className="h-5 w-5 text-amber-600" />
                  <CardTitle>Split Ticket Suggestion</CardTitle>
                </div>
                <CardDescription>
                  This ticket has jobs with a skill tier gap of {splitSuggestion.tierGap || 0} levels. Splitting may improve efficiency.
                </CardDescription>
                <CultureInfluenceNote items={splitSuggestion.cultureInfluence} testId="optiplace-split-culture-influence" />
              </CardHeader>
              <CardContent>
                <Button 
                  variant="outline" 
                  onClick={handlePreviewSplit}
                  className="w-full"
                  disabled={isAnalyzing || isCreating || isSplitting}
                >
                  {isAnalyzing ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Analyzing split options...
                    </>
                  ) : (
                    <>
                      <Scissors className="mr-2 h-4 w-4" />
                      Preview Split Options
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Rankings */}
          {rankings.length > 0 && (
            <div className="space-y-3">
              <h4 className="font-semibold flex items-center gap-2">
                <Trophy className="h-4 w-4 text-primary" />
                Technician Rankings
              </h4>
              
              {/*
                CORE-AB: the ladder is stated once, in the order it is
                applied, so the per-technician sentences below read as
                consequences of a stated rule rather than as assertions. On a
                comeback the same line says that #1 is an override, because
                the list the reader is looking at is not purely the ranking.
              */}
              {/* FLOYD-7 — said ONCE, at the top, because it is a fact about
                  the whole list rather than about any one technician. Without
                  it an advisor reads a normal ranked list and cannot tell that
                  nobody can start on the day they asked about. */}
              {isNextAvailableOnly && rankings.length > 0 && (
                <div
                  className="mb-3 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm"
                  data-testid="optiplace-next-available-only"
                >
                  {rolloverOnlyStartsToday
                    ? 'No technician can finish on the selected date. These options start on it and continue on a later workday.'
                    : 'No technician can start on the selected date. These are the next available options, ranked by when they can actually begin.'}
                </div>
              )}

              {rankings.length > 0 && (
                <p className="mb-2 text-xs text-muted-foreground" data-testid="optiplace-basis-priority">
                  Ranked by: {RANK_KEY_ORDER.map((k, i) => `${i + 1}. ${RANK_KEY_LABEL[k]}`).join(' · ')}.
                  {comebackPinnedTechId
                    ? ' Except #1, which is a comeback override rather than a ranking result.'
                    : ''}
                  {' '}A human makes the final assignment.
                </p>
              )}

              {rankings.map((ranking, idx) => (
                <Card
                  key={ranking.technicianId}
                  data-testid={`optiplace-tech-${ranking.technicianId}`}
                  className={`cursor-pointer transition-all ${
                    selectedTechId === ranking.technicianId
                      ? 'ring-2 ring-primary'
                      : 'hover:bg-accent'
                  }`}
                  onClick={() => setSelectedTechId(ranking.technicianId)}
                >
                  <CardContent className="pt-4">
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Badge variant={idx === 0 ? 'default' : 'secondary'}>
                            #{idx + 1}
                          </Badge>
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-medium">{ranking.technicianName}</p>
                              {ticketData.isComeback && ranking.technicianId === lastTechId && (
                                <span className="text-lg">🚨</span>
                              )}
                              {/* R4 CORE-BC: a position, not a verdict ("best" is judgment language). */}
                              {idx === 0 && <Badge variant="default" className="text-xs">Top of ranking</Badge>}
                              {ranking.dlgState && ranking.dlgState !== 'clear' && (
                                <DlgAdvisoryBadge state={ranking.dlgState} explanation={ranking.dlgExplanation} />
                              )}
                            </div>
                            {/* R4 CORE-BC GAP-001/002/003: the typed explanation, with sample sizes and confidence. */}
                            <AssignmentExplanationView explanation={ranking.explanation} fallbackText={ranking.reasoning} />
                            <CultureInfluenceNote
                              items={ranking.cultureInfluence}
                              testId={`optiplace-culture-influence-${ranking.technicianId}`}
                            />
                          </div>
                        </div>
                        {/*
                          CORE-AB — this number is kept and relabelled, not
                          removed. Whether it should be removed, renamed or
                          replaced by the rank keys is an open PRODUCT question
                          on this capability, and answering it while closing a
                          different item would be deciding something nobody
                          asked for. What is fixed here is the claim it makes:
                          it is a position indicator, assigned after the sort,
                          and it says so.
                        */}
                        {rankIndicatorDisplay(ranking).kind === 'no_history' ? (
                          <div className="text-right" data-testid="optiplace-rank-indicator" data-indicator="no_history">
                            <p className="text-sm font-semibold text-muted-foreground" title={NO_HISTORY_CAVEAT}>
                              {NO_HISTORY_INDICATOR}
                            </p>
                          </div>
                        ) : (
                          <div className="text-right" data-testid="optiplace-rank-indicator" data-indicator="position">
                            <p className="text-2xl font-bold text-primary">{ranking.score}</p>
                            <p
                              className="text-xs text-muted-foreground"
                              title={DISPLAY_SCORE_CAVEAT}
                            >
                              {DISPLAY_SCORE_LABEL}
                            </p>
                          </div>
                        )}
                      </div>

                      <div className="space-y-3">
                        {/* Show Break Adjustment Warning if applicable */}
                        {(ranking as any).breakAdjustment && (
                          <Badge variant="outline" className="mb-2 border-amber-500/50 bg-amber-500/10 text-amber-700 dark:text-amber-400">
                            ⏰ Adjusted after {(ranking as any).breakAdjustment.breakName} ({(ranking as any).breakAdjustment.breakEnd})
                          </Badge>
                        )}

                        {/* Show Split Warning if applicable */}
                        {ranking.willSplit && (
                          <Badge variant="secondary" className="mb-2">
                            ✂️ Split Suggestion - {ranking.splitReason}
                          </Badge>
                        )}

                        {/* Core-100 R4 — CORE-BR: weather context reaches assignment (never a filter). */}
                        <RankingWeatherNotice advisory={ranking.weatherAdvisory} technicianId={ranking.technicianId} />

                        {/* Core-100 R4 — CORE-AU (OD-22): an unverified or unsafe lift pairing is never shown as plainly compatible. */}
                        <RankingLiftNotice ranking={ranking} technicianId={ranking.technicianId} />

                        {/* Past-close advisory (proficiency-king rule):
                            when the predicted finish lands after the
                            shop's close time — OR on a day the shop is
                            normally closed — surface an advisory rather
                            than hiding, down-ranking, or auto-advancing.
                            Rollover flags do NOT suppress this badge; if
                            the rollover banner also renders, the two
                            convey complementary info (work-is-split vs.
                            finish-is-after-hours). Ranking order and
                            selectability are untouched. */}
                        {(() => {
                          const advisory = computeOptiFinishAdvisory(
                            ranking,
                            shopHours,
                            shopTimezone,
                          );
                          if (!advisory?.willRunPastClose) return null;
                          return (
                            <Badge
                              variant="outline"
                              className="mb-2 border-amber-500/50 bg-amber-500/10 text-amber-700 dark:text-amber-400"
                              data-testid={`optiplace-past-close-advisory-${ranking.technicianId}`}
                            >
                              <AlertCircle className="mr-1 h-3 w-3" />
                              {advisory.shopClosedThatDay
                                ? `Strongest match, but the shop is normally closed this day. Estimated finish ${advisory.finishLabel}. Ticket will run outside business hours.`
                                : `Strongest match, but estimated finish ${advisory.finishLabel} is after today's business hours (close ${advisory.closeLabel}). Ticket may roll past close.`}
                            </Badge>
                          );
                        })()}

                        {/* Time Display - Handle both same-day AND rollover candidates */}
                        {(ranking.suggestedStartTime && ranking.suggestedFinishTime) || ranking.isRollover ? (
                          <div className="space-y-2">
                            {/* Show rollover badge prominently */}
                            {/* FLOYD-7 — a start on another day is labelled as
                                one. "Rollover" means the WORK splits; it does not
                                mean the technician cannot start today, and the two
                                were being shown with the same badge. */}
                            {ranking.isFutureDay && (
                              <Badge
                                variant="secondary"
                                className="mb-2"
                                data-testid={`optiplace-next-available-${ranking.technicianId}`}
                              >
                                Next available {ranking.startDateStr ?? 'a later day'}
                              </Badge>
                            )}
                            {(ranking.isRollover || ranking.willRollover) && (
                              <Badge variant="destructive" className="mb-2">
                                🔄 Rollover Required - Work split across multiple days
                              </Badge>
                            )}
                            
                            {/* Two-part rollover: the Day 1 / Day 2 view. Three or more
                                parts use the full list below — the day1/day2 fields are
                                projections of the first two parts only. */}
                            {ranking.isRollover && ranking.day1Minutes && ranking.day2Minutes
                              && (ranking.rolloverParts?.length ?? 0) <= 2 ? (
                              <div className="space-y-2">
                                {/* Day 1 */}
                                <div className="flex items-center gap-4 p-3 rounded-lg bg-muted/50 border border-border">
                                  <div className="flex items-center gap-2">
                                    <Clock className="h-4 w-4 text-muted-foreground" />
                                    <div>
                                      <p className="text-xs text-muted-foreground font-semibold">Day 1 (Today)</p>
                                      {ranking.day1StartTime && ranking.day1FinishTime ? (
                                        <>
                                          <p className="font-medium">
                                            {format(new Date(ranking.day1StartTime), 'h:mm a')} - {format(new Date(ranking.day1FinishTime), 'h:mm a')}
                                          </p>
                                          <p className="text-[10px] text-muted-foreground/70">
                                            {ranking.day1Minutes} min of work
                                          </p>
                                        </>
                                      ) : (
                                        <>
                                          <p className="font-medium">{ranking.day1Minutes} min of work</p>
                                          <p className="text-[10px] text-muted-foreground/70">
                                            From earliest available to EOD
                                          </p>
                                        </>
                                      )}
                                    </div>
                                  </div>
                                </div>
                                
                                {/* Day 2 */}
                                <div className="flex items-center gap-4 p-3 rounded-lg bg-warning/10 border border-warning/30">
                                  <div className="flex items-center gap-2">
                                    <Clock className="h-4 w-4 text-warning" />
                                    <div>
                                      <p className="text-xs text-warning font-semibold">Day 2 (Tomorrow+)</p>
                                      {ranking.day2StartTime && ranking.day2FinishTime ? (
                                        <>
                                          <p className="font-medium">
                                            {format(new Date(ranking.day2StartTime), 'h:mm a')} - {format(new Date(ranking.day2FinishTime), 'h:mm a')}
                                          </p>
                                          <p className="text-[10px] text-muted-foreground/70">
                                            {ranking.day2Minutes} min of work
                                          </p>
                                        </>
                                      ) : (
                                        <>
                                          <p className="font-medium">{ranking.day2Minutes} min of work</p>
                                          <p className="text-[10px] text-muted-foreground/70">
                                            Starting next working day
                                          </p>
                                        </>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ) : (ranking.willRollover || ranking.isRollover) && ranking.rolloverParts && ranking.rolloverParts.length > 0 ? (
                              // Display all rollover parts with full timestamps
                              ranking.rolloverParts.map((part, partIdx) => (
                                <div 
                                  key={part.partLabel}
                                  className={`flex items-center gap-4 p-3 rounded-lg ${
                                    partIdx === 0 
                                      ? 'bg-muted/50' 
                                      : 'bg-warning/10 border border-warning/30'
                                  }`}
                                >
                                  <div className="flex items-center gap-2">
                                    <Clock className={`h-4 w-4 ${partIdx === 0 ? 'text-muted-foreground' : 'text-warning'}`} />
                                    <div>
                                      <p className="text-xs text-muted-foreground">
                                        {part.partLabel} - Start
                                      </p>
                                      <p className="font-medium">
                                        {format(new Date(part.startTime), 'h:mm a')}
                                      </p>
                                      <p className="text-[10px] text-muted-foreground/70">
                                        {part.scheduledDate ?? (partIdx === 0 ? 'Today' : `Day +${part.dayOffset}`)}
                                      </p>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <Clock className={`h-4 w-4 ${partIdx === 0 ? 'text-muted-foreground' : 'text-warning'}`} />
                                    <div>
                                      <p className="text-xs text-muted-foreground">
                                        {part.partLabel} - Finish
                                      </p>
                                      <p className="font-medium">
                                        {format(new Date(part.finishTime), 'h:mm a')}
                                      </p>
                                      <p className="text-[10px] text-muted-foreground/70">
                                        {part.minutes} min
                                      </p>
                                    </div>
                                  </div>
                                  {partIdx === 0 && (
                                    <div className="flex items-center gap-2">
                                      <Zap className="h-4 w-4 text-primary" />
                                      <div>
                                        <p className="text-xs text-muted-foreground">Tech OptiTime</p>
                                        <p className="font-medium">{ranking.optiTime} min</p>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              ))
                            ) : ranking.suggestedStartTime && ranking.suggestedFinishTime ? (
                              // No rollover - show single time block
                              <div className="flex items-center gap-4 p-3 bg-muted/50 rounded-lg">
                                <div className="flex items-center gap-2">
                                  <Clock className="h-4 w-4 text-muted-foreground" />
                                  <div>
                                    <p className="text-xs text-muted-foreground">Start Time</p>
                                    <p className="font-medium">
                                      {format(new Date(ranking.suggestedStartTime), 'h:mm a')}
                                    </p>
                                    <p className="text-[10px] text-muted-foreground/70">
                                      Earliest slot after existing work
                                    </p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Clock className="h-4 w-4 text-muted-foreground" />
                                  <div>
                                    <p className="text-xs text-muted-foreground">Finish Time</p>
                                    <p className="font-medium">
                                      {format(new Date(ranking.suggestedFinishTime), 'h:mm a')}
                                    </p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Zap className="h-4 w-4 text-primary" />
                                  <div>
                                    <p className="text-xs text-muted-foreground">Tech OptiTime</p>
                                    <p className="font-medium">{ranking.optiTime} min</p>
                                  </div>
                                </div>
                              </div>
                            ) : null}
                          </div>
                        ) : null}

                        {/* Show Split Breakdown if applicable */}
                        {ranking.willSplit && ranking.splitPartA && ranking.splitPartB && (
                          <div className="space-y-2 mt-3">
                            <p className="text-xs font-semibold text-muted-foreground">Suggested Split:</p>
                            
                            {/* Part A */}
                            <div className="flex items-center gap-4 p-3 bg-primary/10 border border-primary/30 rounded-lg">
                              <div className="flex items-center gap-2">
                                <Wrench className="h-4 w-4 text-primary" />
                                <div>
                                  <p className="text-xs text-muted-foreground">Part A</p>
                                  <p className="font-medium">{ranking.splitPartA.jobCount} jobs</p>
                                  <p className="text-[10px] text-muted-foreground/70">
                                    Skill Lvl {ranking.splitPartA.avgSkillLevel}
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <Clock className="h-4 w-4 text-primary" />
                                <div>
                                  <p className="text-xs text-muted-foreground">Duration</p>
                                  <p className="font-medium">{ranking.splitPartA.minutes} min</p>
                                </div>
                              </div>
                            </div>

                            {/* Part B */}
                            <div className="flex items-center gap-4 p-3 bg-secondary/10 border border-secondary/30 rounded-lg">
                              <div className="flex items-center gap-2">
                                <Wrench className="h-4 w-4 text-secondary" />
                                <div>
                                  <p className="text-xs text-muted-foreground">Part B</p>
                                  <p className="font-medium">{ranking.splitPartB.jobCount} jobs</p>
                                  <p className="text-[10px] text-muted-foreground/70">
                                    Skill Lvl {ranking.splitPartB.avgSkillLevel}
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <Clock className="h-4 w-4 text-secondary" />
                                <div>
                                  <p className="text-xs text-muted-foreground">Duration</p>
                                  <p className="font-medium">{ranking.splitPartB.minutes} min</p>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}

                        {/*
                          CORE-AB — THE BASIS THIS CANDIDATE WAS ACTUALLY
                          RANKED ON.

                          smart-assign sorts lexicographically: proficiency BAND
                          → current workload → earliest start → historical
                          affinity → a tie-break. Not a weighted blend. A later
                          key is consulted only when every earlier one ties, so
                          exactly ONE key settles each position and naming it is
                          the whole of what a reader is owed.

                          Built against the candidate immediately above in this
                          same list, because a lexicographic position is settled
                          against the neighbour rather than against the field.
                        */}
                        {(() => {
                          // The pin is not a placement, so the technician
                          // below it is compared against nothing — exactly as
                          // they would be with the pinned technician absent.
                          const isPinned =
                            comebackPinnedTechId === ranking.technicianId;
                          const basis = describeRankingBasis(
                            ranking,
                            rankingNeighbour(rankings, idx, comebackPinnedTechId),
                            { pinned: isPinned },
                          );
                          if (basis.pinned) {
                            return (
                              <p
                                className="text-xs text-muted-foreground"
                                data-testid="optiplace-ranking-basis"
                                data-decided-by="comeback-override"
                              >
                                {basis.explanation}
                              </p>
                            );
                          }
                          if (basis.isTop) {
                            return (
                              <p
                                className="text-xs text-muted-foreground italic"
                                data-testid="optiplace-ranking-basis"
                                data-decided-by="top"
                              >
                                Ranked first. Nothing above to compare against.
                              </p>
                            );
                          }
                          return (
                            <p
                              className="text-xs text-muted-foreground"
                              data-testid="optiplace-ranking-basis"
                              data-decided-by={basis.decidedBy ?? 'tie'}
                            >
                              {basis.explanation}
                            </p>
                          );
                        })()}

                        {/* Metrics Grid */}
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div>
                            <div className="flex justify-between mb-1">
                              <span className="text-muted-foreground">Proficiency</span>
                              <span className="font-medium">
                                {ranking.proficiencyMatch == null ? 'No OptiBay history yet' : `${ranking.proficiencyMatch}%`}
                              </span>
                            </div>
                            {ranking.proficiencyMatch != null && <Progress value={ranking.proficiencyMatch} className="h-2" />}
                          </div>
                          <div>
                            <div className="flex justify-between mb-1">
                              <span className="text-muted-foreground">Available Time</span>
                              <span className="font-medium">{ranking.availabilityPercentage || ranking.availabilityScore}%</span>
                            </div>
                            <Progress value={ranking.availabilityPercentage || ranking.availabilityScore} className="h-2" />
                          </div>
                          <div>
                            <div className="flex justify-between mb-1">
                              <span className="text-muted-foreground">Current Workload</span>
                              <span className="font-medium">{ranking.workloadPercentage || (100 - (ranking.workloadScore || 0))}%</span>
                            </div>
                            <Progress value={ranking.workloadPercentage || (100 - (ranking.workloadScore || 0))} className="h-2" />
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-muted-foreground">Bay Match</span>
                            {/* CORE-AU: a bay match is not a safe pairing while the lift is unverified or too small. */}
                            <Badge
                              variant={ranking.bayMatch && !rankingLiftNeedsAttention(ranking) ? 'default' : 'secondary'}
                              data-testid={`optiplace-bay-match-${ranking.technicianId}`}
                            >
                              {ranking.bayMatch
                                ? (ranking.liftSafetyBlock ? 'Yes — no safe lift'
                                  : ranking.liftVerificationRequired && ranking.liftVerificationBasis === 'lift_rating_unknown' ? 'Yes — capacity not verified'
                                  : ranking.liftVerificationRequired ? 'Yes — lift not verified' : 'Yes')
                                : 'No'}
                            </Badge>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}

              {/*
                CORE-AB: a hard constraint FILTERS. These technicians are not
                at the bottom of the list — they are not in it, and the reason
                is a constraint rather than a judgement about their capability.
                Saying "absent" and saying "worst" are different statements
                about a person, and only one of them is true here.
              */}
              {rankingExclusions.length > 0 && (
                <div className="mt-3 rounded-md border border-border/60 bg-muted/30 p-3">
                  <p className="text-xs font-medium text-muted-foreground">
                    Not ranked for this ticket
                  </p>
                  <ul className="mt-1 space-y-1">
                    {rankingExclusions.map((ex) => (
                      <li key={ex.technicianId} className="text-xs text-muted-foreground">
                        <span className="font-medium text-foreground">
                          {ex.technicianName || ex.technicianId}
                        </span>
                        {' — '}{ex.explanation}
                      </li>
                    ))}
                  </ul>
                </div>
              )}


              <div className="flex gap-3 pt-4">
                <Button variant="outline" onClick={onClose} className="flex-1">
                  Cancel
                </Button>
                <Button 
                  onClick={() => {
                    // Check if selected tech is saturated — show override modal
                    const selectedRanking = rankings.find(r => r.technicianId === selectedTechId);
                    if (selectedRanking?.dlgState === 'saturated') {
                      setPendingDlgTechId(selectedTechId);
                      setDlgOverrideOpen(true);
                      return;
                    }
                    createTicketWithAssignment();
                    // Auto-advance tutorial from step 11 to step 12
                    if (currentStepData?.id === 'optiplace-modal') {
                      setTimeout(() => nextStep(), 100);
                    }
                  }}
                  disabled={isCreating || !selectedTechId}
                  className="flex-1"
                  data-create-ticket-btn
                  data-testid="optiplace-confirm-btn"
                >
                  {isCreating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {ticketData.id ? 'Reassign Ticket' : 'Create Ticket with Assignment'}
                </Button>
              </div>
            </div>
          )}
        </div>

        <CalendarModal
          open={isCalendarOpen}
          onOpenChange={setIsCalendarOpen}
          currentDate={selectedDate}
          onDateSelect={(dayLabel) => {
            setSelectedDayLabel(dayLabel);
            setIsCalendarOpen(false);
          }}
        />

        <SplitPreviewDialog
          isOpen={showSplitPreview}
          onClose={() => setShowSplitPreview(false)}
          splitOptions={splitOptions.length > 0 ? [splitOptions[currentSplitOptionIndex]] : []}
          onConfirm={handleConfirmSplit}
          isProcessing={isSplitting}
          onViewNextOption={handleViewNextOption}
          currentOptionIndex={currentSplitOptionIndex}
          totalOptions={splitOptions.length}
        />

        <DlgOverrideModal
          open={dlgOverrideOpen}
          onOpenChange={setDlgOverrideOpen}
          onConfirm={(reason: DlgOverrideReason, note: string) => {
            setDlgOverrideOpen(false);
            setPendingDlgTechId(null);
            console.log('[DLG-OVERRIDE] Confirmed:', { reason, note, techId: pendingDlgTechId });
            createTicketWithAssignment();
            if (currentStepData?.id === 'optiplace-modal') {
              setTimeout(() => nextStep(), 100);
            }
          }}
          techName={rankings.find(r => r.technicianId === pendingDlgTechId)?.technicianName || ''}
          dlgExplanation={rankings.find(r => r.technicianId === pendingDlgTechId)?.dlgExplanation}
          isLoading={isCreating}
        />
      </DialogContent>
    </Dialog>
  );
};
